/**
 * Typed fetch wrappers for the dashboard's `/api/*` endpoints.
 *
 * NOT every call goes through a named wrapper here: `api.get<T>(path)` is a
 * generic escape hatch several pages use directly (`Flows`, `FlowEdit`,
 * `EmailTemplates`, …). So the absence of a named method for an endpoint does
 * NOT mean the endpoint is unused — grep the PATH STRING across the web tree,
 * not the method name, before concluding a route is dead. Prettier also breaks
 * long calls as `api\n  .method()`, which a single-line `api.method(` grep
 * misses; strip newlines when searching for call sites.
 */

import { supabase } from "./supabase";
import { failureFields, type FailureField } from "./field-errors";
import { reportApiFailure } from "./session-lost";
import { studioHeaders } from "./studio-scope";
import type { NewsletterJoinedVia } from "@layer/canonical-data/newsletter-subscribers";
import type {
  CatalogResponse,
  DataResponse,
  SagasResponse,
  WhoamiResponse,
} from "@layer/dashboard/types";

/**
 * The bearer for every `/api/*` call. With no local session there is nothing
 * to send: an empty `Authorization: Bearer ` used to reach the server and come
 * back as a 401 `auth_scheme_invalid` — the same dead end, one round-trip
 * later. Throw the 401 the server would have sent, but do NOT report it as a
 * lost session: no local session means we are already signed out on this
 * device (the SIGNED_OUT event has `RequireAuth` on its way to /login), and
 * reporting it would turn a deliberate Logout into a "session ended" banner.
 */
async function bearer(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token ?? "";
  if (token.length === 0) {
    throw new ApiError(401, "session_missing", "api_failed:401:session_missing");
  }
  return token;
}

/**
 * Structured API failure. Carries the HTTP status and the server's
 * stable `code` field when the response body parses as JSON. Callers
 * can `instanceof` this to branch on `code` (e.g. handle
 * `studio_not_found` / `studio_stale_session` by signing out and
 * routing to /login with a stale-session banner).
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    message: string,
    /**
     * field-errors (2026-11-01) — the field a refusal names for staff, and
     * the fix (the staff allowlist save's), when the body carries one.
     */
    public readonly fields: ReadonlyArray<FailureField> | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Every authed call carries the studio this tab is mounted in as
// `X-Layer-Studio` (`studio-scope.ts`) — the JWT proves the person, the
// header selects the studio, and the server validates the pair
// (staff-person-login-studio-switcher, 2026-09-27).
async function authedGet<T>(path: string): Promise<T> {
  const res = await authedGetResponse(path);
  return (await res.json()) as T;
}

/**
 * 2026-10-23 — a GET whose answer is a file, not JSON ("Open PDF" on
 * `/trainer-performance`). A plain link cannot carry the bearer, so the page
 * fetches the bytes and opens them as a Blob URL.
 */
async function authedGetBlob(path: string): Promise<Blob> {
  const res = await authedGetResponse(path);
  return res.blob();
}

/** The authenticated GET both of the above share; a failure throws `ApiError`. */
async function authedGetResponse(path: string): Promise<Response> {
  const token = await bearer();
  const res = await fetch(path, {
    headers: { Authorization: `Bearer ${token}`, ...studioHeaders() },
  });
  if (!res.ok) {
    let code: string | null = null;
    try {
      const body = (await res.json()) as { code?: unknown };
      if (typeof body?.code === "string") code = body.code;
    } catch {
      // Body wasn't JSON — leave code = null.
    }
    // A lost session (401, or a stale-studio 403) leaves the app here — once,
    // for every hook that fails at the same moment (staff-session-slice-1).
    // The throw below still reaches the caller so its own error rendering
    // holds until the bounce unmounts it.
    reportApiFailure(res.status, code);
    throw new ApiError(
      res.status,
      code,
      `api_failed:${res.status}${code ? `:${code}` : ""}`,
    );
  }
  return res;
}

async function authedPost<T>(path: string, body: unknown): Promise<T> {
  const token = await bearer();
  const res = await fetch(path, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...studioHeaders(),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let code: string | null = null;
    let fields: ReadonlyArray<FailureField> | null = null;
    try {
      const responseBody = (await res.json()) as { code?: unknown; fields?: unknown };
      if (typeof responseBody?.code === "string") code = responseBody.code;
      fields = failureFields(responseBody?.fields);
    } catch {
      // Body wasn't JSON — leave code = null.
    }
    reportApiFailure(res.status, code);
    throw new ApiError(
      res.status,
      code,
      `api_failed:${res.status}${code ? `:${code}` : ""}`,
      fields,
    );
  }
  return (await res.json()) as T;
}

/**
 * wa-activate-template-check (2026-10-28) — one template the studio can send,
 * as the `/whatsapp-signup` check holds it against the new (parked) account
 * and today's. Mirrors `TemplateCheckRow` in
 * `apps/dashboard/src/whatsapp-template-inventory.ts`.
 */
export interface WhatsappSignupTemplateRow {
  readonly name: string;
  readonly language: string;
  readonly sources: ReadonlyArray<{
    readonly kind: "code" | "catalog" | "config" | "flow";
    readonly label: string;
  }>;
  readonly catalog_category: string | null;
  readonly new: { readonly status: string; readonly category: string } | null;
  readonly live: { readonly status: string; readonly category: string } | null;
  readonly ready: boolean;
  readonly category_changed: boolean;
}

/**
 * 2026-08-24 — one `oauth_clients` row on the /mcp panel wire. The
 * public projection: the scrypt `client_secret_hash` NEVER leaves the
 * server (list, create, update and revoke all strip it).
 */
export interface OauthClientRow {
  readonly id: string;
  readonly studio_id: string;
  readonly client_id: string;
  readonly name: string;
  readonly granted_scopes: ReadonlyArray<string>;
  /** Migration 223 — `staff` = the connector acts as the person in `staff_id`. */
  readonly acts_as: "agent" | "staff";
  readonly staff_id: string | null;
  /** `staff` = held at the staff tier even while its person is an admin. */
  readonly tier_cap: "staff" | null;
  /**
   * Migration 226 — the tools this connector switched off on /mcp, its tool
   * boxes. Only a person-bound connector has any; an agent grant's is empty.
   */
  readonly disabled_tools: ReadonlyArray<string>;
  readonly revoked_at: string | null;
  readonly last_used_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  /**
   * LIST rows only, and only for a person-bound connector (null for an agent
   * grant): who it acts as right now, read from the live staff row.
   * `display_name` and `tier` are null unless `lookup` is `present`.
   */
  readonly person?: OauthClientPerson | null;
  /**
   * LIST rows only, and only for an ACTIVE person-bound connector (null for an
   * agent grant or a revoked connector): one box per tool its tier could ever
   * list, at the tier it acts with now.
   */
  readonly tools?: OauthClientTools | null;
}

/** `mcp-connector-tool-boxes` (2026-10-14) — a connector's tool boxes. */
export interface OauthClientTools {
  /**
   * `present` = drawn from the studio's live chat tool lists. `unavailable` =
   * those lists could not be read, or who the connector acts as is not known
   * right now (`person.lookup` says which); `boxes` is then null.
   */
  readonly lookup: "present" | "unavailable";
  readonly boxes: ReadonlyArray<OauthClientToolBox> | null;
}

/** One tool on a connector, on only when BOTH switches are. */
export interface OauthClientToolBox {
  readonly name: string;
  /** Config's chat tool lists give it to the connector's tier. */
  readonly on_config: boolean;
  /** The connector has not switched it off on /mcp. */
  readonly on_connector: boolean;
}

export interface OauthClientPerson {
  /**
   * `present` = on the staff list now. `missing` = no longer on it (removing
   * someone revokes their connectors, so this is rare on a live row).
   * `unavailable` = the staff list could not be read; nothing is known.
   */
  readonly lookup: "present" | "missing" | "unavailable";
  readonly display_name: string | null;
  readonly tier: "staff" | "admin" | null;
  readonly is_you: boolean;
}

/** The signed-in person as a connector would act as them; null = no staff row. */
export interface OauthClientsViewer {
  readonly display_name: string;
  readonly tier: "staff" | "admin";
}

export interface DevResetResponse {
  readonly ok: boolean;
  readonly tables_purged: number;
  readonly members_deleted: number;
  readonly pa_actions_deleted: number;
  readonly peter_actions_kept: number;
  readonly inbox_actions_kept: number;
  readonly inbox_threads_deleted: number;
}

/** `POST /api/dev/purge-members` — mirrors `MemberPurgeResult` (member-purge.ts). */
export interface MemberPurgeResponse {
  readonly ok: boolean;
  readonly members_deleted: number;
  readonly rows_deleted: Readonly<Record<string, number>>;
  readonly messages_deleted: number;
  readonly pr_messages_kept: number;
  readonly inbox_threads_deleted: number;
  readonly tasks_unassigned: number;
  readonly slots_healed: number;
}

// --- Calendar (today / week / month slot views) ------------------------------

export interface CalendarBooking {
  readonly id: string;
  readonly member_id: string | null;
  readonly member_name: string | null;
  readonly status: string;
  /** Session type recorded on the booking (e.g. "studio" / "home"). */
  readonly session_type: string | null;
  /** Lifecycle stage (purchase_count >= 2 → customer), mirrors `/today`. */
  readonly lifecycle: "trial" | "customer";
  readonly member_status: string | null;
  readonly member_phone: string | null;
  readonly member_email: string | null;
  /** The member's live credit — usable packages + usable memberships. */
  readonly remaining_credits: number;
}

export interface CalendarSlot {
  readonly id: string;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly capacity: number;
  readonly taken_count: number;
  readonly status: string;
  /** Hidden from client booking (migration 060). Still active + shown to staff. */
  readonly hidden: boolean;
  readonly location: string | null;
  readonly trainer_id: string;
  readonly trainer_name: string | null;
  readonly bookings: ReadonlyArray<CalendarBooking>;
}

export interface CalendarResponse {
  readonly from: string;
  readonly to: string;
  readonly slots: ReadonlyArray<CalendarSlot>;
  readonly computed_at: string;
}

// --- Availability paste-tool (POST /api/availability/parse) -------------------

export interface AvailabilityWindowProposal {
  readonly weekday: number; // 0=Sun..6=Sat
  readonly start_time: string;
  readonly end_time: string;
  readonly location: string | null;
  readonly session_type: string | null;
  readonly capacity: number | null;
  // Migration 059 — date bounds (both null = permanent weekly window).
  readonly effective_from: string | null;
  readonly effective_until: string | null;
  readonly bound_label: string | null;
}
export interface TrainerAvailabilityProposal {
  readonly trainer_id: string;
  readonly trainer_name: string;
  readonly windows: ReadonlyArray<AvailabilityWindowProposal>;
}
export interface AvailabilityParseResponse {
  readonly ok: true;
  readonly proposals: ReadonlyArray<TrainerAvailabilityProposal>;
  readonly unmatched_names: ReadonlyArray<string>;
  readonly notes: string | null;
  readonly model: string;
}

// --- Bulk edit/delete tool (POST /api/slots/parse-bulk) ----------------------

export type BulkOpKind =
  | "retime"
  | "change_trainer"
  | "change_capacity"
  | "hide"
  | "unhide"
  | "delete";

export interface BulkOpCurrent {
  readonly starts_at: string;
  readonly ends_at: string;
  readonly trainer_id: string;
  readonly trainer_name: string | null;
  readonly capacity: number;
  readonly taken_count: number;
  readonly hidden: boolean;
  readonly location: string | null;
  readonly local_label: string;
}
export interface BulkSlotOp {
  readonly op: BulkOpKind;
  readonly slot_id: string;
  /** Index into `rules[]` of the selector rule that produced this row. */
  readonly rule_index: number;
  readonly current: BulkOpCurrent;
  readonly starts_at?: string;
  readonly ends_at?: string;
  readonly trainer_id?: string;
  readonly trainer_name?: string;
  readonly capacity?: number;
  readonly summary: string;
  readonly warnings: ReadonlyArray<string>;
  /** Non-null = the row cannot be applied (e.g. a delete of a booked slot). */
  readonly blocked: string | null;
}
/**
 * One selector rule as the server expanded it (2026-09-02: the model returns a
 * few rules — "delete · Mario · 1–31 Jan 2027" — and the server picks the
 * slots, so `ops` are grouped under these). Mirrors `BulkRuleSummary` in
 * `apps/dashboard/src/slot-bulk-rules.ts`.
 */
export interface BulkRuleSummary {
  readonly index: number;
  readonly op: BulkOpKind;
  readonly summary: string;
  /** Slots the selector matched (before per-slot blocks / no-op drops). */
  readonly matched: number;
  /** Rows that can be applied (matched minus blocked minus no-ops). */
  readonly applicable: number;
  /** Non-null = the whole rule produced no rows, and why. */
  readonly blocked_reason: string | null;
  readonly warnings: ReadonlyArray<string>;
  /** Server-computed: a large rule whose rows start UNTICKED until acknowledged. */
  readonly requires_ack: boolean;
}
export interface SlotBulkParseUsage {
  readonly input_tokens: number;
  readonly output_tokens: number;
  readonly stop_reason: string;
}
export interface SlotBulkParseResponse {
  readonly ok: true;
  readonly ops: ReadonlyArray<BulkSlotOp>;
  readonly rules: ReadonlyArray<BulkRuleSummary>;
  readonly notes: string | null;
  readonly model: string;
  readonly usage: SlotBulkParseUsage;
}

// --- Data-browser per-row delete (dependency-aware) --------------------------

export interface DeleteDependent {
  readonly table: string;
  readonly column: string;
  readonly count: number;
}

/** Rows a DB detach trigger will KEEP (FK nulled) when the row is deleted. */
export interface DeleteKept {
  readonly table: string;
  readonly column: string;
  readonly count: number;
  readonly label: string;
}

export interface DeleteInspection {
  readonly entity: string;
  readonly id: string;
  readonly exists: boolean;
  readonly dependents: ReadonlyArray<DeleteDependent>;
  /** Survivors — e.g. cancelled bookings detached from a deleted slot. */
  readonly kept: ReadonlyArray<DeleteKept>;
  readonly deletable: boolean;
}

export interface DeleteResult {
  readonly ok: boolean;
  readonly deleted: number;
  readonly deletedByTable?: Record<string, number>;
  readonly blockers?: ReadonlyArray<DeleteDependent>;
  /**
   * The id whose dependents blocked a multi-row delete (`has_dependents` on
   * the `ids` wire only). `blockers` names the TABLES; this names the ROW, so
   * a caller holding many ids can drop it and resubmit the rest.
   */
  readonly blockedId?: string;
  readonly code?: string;
}

export interface DataRowDeleteResponse {
  readonly ok: boolean;
  readonly inspection?: DeleteInspection;
  readonly result?: DeleteResult;
  readonly code?: string;
  readonly message?: string;
}

/**
 * POST /api/data-row/:entity — dependency check / delete / cascade. Returns the
 * full response body for 200/409/404/400 (so the caller can render the
 * dependent report on a 409); throws `ApiError` only on auth failures
 * (401 unauthorized / 410 stale session) so the app can sign the user out.
 */
export async function dataRowDelete(
  entity: string,
  id: string,
  mode: "check" | "delete" | "cascade",
): Promise<DataRowDeleteResponse> {
  return dataRowDeleteRequest(entity, { id, mode });
}

/**
 * Multi-select wire (2026-07-28). `ids` replaces `id` for a selection.
 * Emptying a table is `dataRowDeleteAll` below — `mode: "all"` +
 * `confirm: true` (an explicit boolean acknowledgement, not a typed echo of
 * the entity name; simplified 2026-07-29), which the server rejects without
 * the flag, so a mis-fired request can never empty a table on its own.
 */
export async function dataRowDeleteMany(
  entity: string,
  ids: ReadonlyArray<string>,
  mode: "check" | "delete",
): Promise<DataRowDeleteResponse> {
  return dataRowDeleteRequest(entity, { ids, mode });
}

export async function dataRowDeleteAll(
  entity: string,
): Promise<DataRowDeleteResponse> {
  // `confirm: true` is an explicit acknowledgement flag, not a typed echo of
  // the table name (simplified 2026-07-29). The server rejects the request
  // without it, so a stray POST cannot empty a table.
  return dataRowDeleteRequest(entity, { mode: "all", confirm: true });
}

async function dataRowDeleteRequest(
  entity: string,
  payload: Record<string, unknown>,
): Promise<DataRowDeleteResponse> {
  const token = await bearer();
  const res = await fetch(`/api/data-row/${entity}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...studioHeaders(),
    },
    body: JSON.stringify(payload),
  });
  const body = (await res
    .json()
    .catch(() => ({ ok: false }))) as DataRowDeleteResponse;
  if (!res.ok) reportApiFailure(res.status, body.code ?? null);
  if (res.status === 401 || res.status === 410) {
    throw new ApiError(res.status, body.code ?? null, `api_failed:${res.status}`);
  }
  return body;
}

/**
 * Phase 41 — per-studio config + sections metadata returned by
 * `GET /api/studio-config/:slug`. The SPA's `/config/:slug` editor
 * page renders one card per section using this metadata + the parsed
 * L2 YAML shape.
 */
export interface StudioConfigSectionMetadata {
  readonly id: string;
  readonly title: string;
  readonly fields: ReadonlyArray<string>;
  readonly advanced: boolean;
  readonly hidden_when_layer_canonical_booking?: boolean;
}

export interface StudioConfigResponse {
  readonly config: Record<string, unknown>;
  readonly studio_slug: string;
  readonly sections_metadata: ReadonlyArray<StudioConfigSectionMetadata>;
  /**
   * Phase 66c — the CURRENT code-default prompt text (skeleton + the two
   * instruction blocks, in editable template form). The System prompt
   * editor section pre-seeds each textarea with these when the studio has
   * no override, so operators edit FROM the real prompt.
   */
  readonly prompt_defaults?: {
    readonly system_prompt: string;
    readonly customer_instructions: string;
    readonly staff_instructions: string;
  };
}

export interface StudioConfigSaveResponse {
  readonly ok: boolean;
  readonly outcome: string;
  readonly response: {
    readonly written: boolean;
    readonly studio_slug: string;
    readonly fields_changed: ReadonlyArray<string>;
    readonly files_changed: number;
  };
  readonly idempotency_replay_short_circuit?: boolean;
}

// --- Member detail page (GET /api/members/:id) -------------------------------
// Mirrors the server's `member-detail.ts` shapes. These carry member PII
// (name / email / phone / message bodies / payment notes) — staff-facing, same
// as the /data/members browser — which is why they live here + in the repo
// file rather than in the PII-free shared `@layer/dashboard/types`.

export interface MemberDetailAccount {
  readonly id: string;
  readonly display_name: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly status: string;
  readonly consent: Record<string, unknown>;
  readonly metadata: Record<string, unknown>;
  readonly created_at: string;
  readonly updated_at: string;
  /**
   * When `delete_member` erased this member (ISO), `null` if never (box 17).
   * The page shows "Erased on <date>" and turns every editor off.
   */
  readonly erased_at: string | null;
  readonly available_credits: number;
  /** `available_credits` split by catalog session_type; studio + home === total. */
  readonly available_credits_studio: number;
  readonly available_credits_home: number;
  readonly lifetime_spent_minor: number;
  readonly total_bookings: number;
  readonly cancelled_bookings: number;
  readonly payment_count: number;
  readonly favorite_trainer: string | null;
  /** Instagram @handle (no leading `@`) from Meta, or null when unlinked. */
  readonly instagram_handle: string | null;
  readonly next_session_at: string | null;
  readonly upcoming_payment_at: string | null;
  readonly currency: string | null;
  /** Total session credits ever acquired (live balance + sessions consumed). */
  readonly purchased_credits_total: number;
  /** Count of settled money-in payments (charges + invoices; excludes refunds). */
  readonly successful_payment_count: number;
  /** Expected future subscription revenue (minor units) = remaining cycles × per-cycle amount. */
  readonly upcoming_revenue_minor: number;
  /** Most-recent Resend email event + ISO timestamp, or null when unmatched (migration 113). */
  readonly last_email_event: LastEmailEvent | null;
  /**
   * Whether a send to this member would be REFUSED right now, per channel, and
   * since when. Mirrors the server's `MemberDeliverability`.
   */
  readonly deliverability: MemberDeliverability;
  /** Where the member stands with the email newsletter. Mirrors the server's `MemberNewsletterStanding`. */
  readonly newsletter: MemberNewsletterStanding;
  /**
   * When and how they joined the newsletter, by the subscriber list's rule;
   * `null` when the read failed. Mirrors the server's `MemberNewsletterJoin`.
   */
  readonly newsletter_join: { readonly at: string; readonly via: NewsletterJoinedVia } | null;
}

/**
 * The member's standing with the email newsletter, as `resubscribe_newsletter`
 * plans it (newsletter-resubscribe, 2026-10-23): it reaches them; signing up
 * again would bring them back by clearing `clears`; a reason a sign-up never
 * answers (the reach rule's code); or the read failed. `test_editions_only`
 * (newsletter-test-resubscribe, 2026-10-30): a test account, whose newsletter
 * is the test editions and never a real one.
 */
export type MemberNewsletterStanding =
  | { readonly state: "subscribed"; readonly test_editions_only: boolean }
  | {
      readonly state: "resubscribable";
      readonly test_editions_only: boolean;
      readonly clears: ReadonlyArray<string>;
      readonly other_marketing_resumes: boolean;
    }
  | { readonly state: "blocked"; readonly reason: string }
  | { readonly state: "unknown" };

/**
 * Per-channel read-time refusal verdict — the same reads and bars
 * `send_whatsapp_message` and `send_email` ask before they call a vendor, so
 * the profile and the send path can never disagree.
 *
 * A verdict, NOT a stored flag: nothing is written when a recipient goes bad
 * and nothing has to be un-set when they come good. The refusal is a pause
 * that grows with each failure — 7, 30, then 90 days from the latest — and
 * lifts at once on a sign of life (deliverability-ladder, 2026-10-31). Which is
 * exactly why the page shows the DATE and the step: "this address is being
 * refused" is not actionable without "until when".
 *
 * `null` on a channel means no refusal — no such identifier, or no pause
 * running.
 */
export interface MemberDeliverability {
  /**
   * Whether both reads were actually asked and answered. `false` means a
   * substrate fault swallowed the lookup, and the fields below are "unknown",
   * NOT "clean".
   */
  readonly checked: boolean;
  /** ISO-8601 of the latest Meta 131026 on this number, while its pause runs. */
  readonly whatsapp_undeliverable_since: string | null;
  /** When the WhatsApp pause ends, so the page need not do date maths. */
  readonly whatsapp_refusal_lapses_at: string | null;
  /** Which failure this is since the number last worked (1, 2, 3 …), while refused. */
  readonly whatsapp_failure_step: number | null;
  /** ISO-8601 of the latest bounce on this address, while `send_email` refuses it. */
  readonly email_undeliverable_since: string | null;
  /** When the email pause ends; `null` while the account is gone (refused until the address changes). */
  readonly email_refusal_lapses_at: string | null;
  /** Which bounce this is since the address last took a delivery (1, 2, 3 …), while refused. */
  readonly email_failure_step: number | null;
  /** The receiving server said the account does not exist or is disabled. */
  readonly email_account_gone: boolean;
}

/** A member's most-recent Resend email event (mirrors `@layer/canonical-actions`). */
export interface LastEmailEvent {
  /** delivered | opened | clicked | bounced | complained. */
  readonly type: string;
  /** ISO-8601 timestamp of that most-recent event. */
  readonly at: string;
}

/** One research source behind an AI enrichment (web URL or "first_party"). */
export interface AiEnrichmentCitation {
  readonly url: string;
  readonly title: string | null;
}

/**
 * AI-enrichment block stored at `members.metadata.ai_enrichment` by the
 * `enrich_member` action. Inferred-profile fields + provenance. Every inferred
 * field is nullable — the model may fail to infer any single one. Mirrors the
 * backend's `MemberAiEnrichment` shape.
 */
export interface AiEnrichment {
  readonly inferred_gender: string | null;
  readonly inferred_age_band: string | null;
  readonly inferred_primary_goal: string | null;
  readonly inferred_objection: string | null;
  readonly inferred_job: string | null;
  readonly inferred_employer: string | null;
  readonly inferred_location: string | null;
  readonly linkedin_url: string | null;
  readonly instagram_handle: string | null;
  readonly social_links: Record<string, string> | null;
  readonly summary: string | null;
  readonly suggested_next_action: string | null;
  readonly confidence_per_field: Record<string, number>;
  readonly overall_confidence: number | null;
  readonly research_citations: ReadonlyArray<AiEnrichmentCitation>;
  // provenance
  readonly model: string;
  readonly enriched_at: string;
  readonly cost_usd: number;
  readonly source: string;
}

export interface MemberDetailAppointment {
  readonly id: string;
  readonly slot_id: string;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly status: string;
  readonly attendance_status: string;
  readonly session_type: string | null;
  readonly location: string | null;
  readonly trainer_name: string | null;
  readonly rating: number | null;
  readonly rating_comment: string | null;
  /**
   * Slice 4 — for a cancelled session that consumed a restorable credit,
   * whether it was `'returned'` to source or `'kept'` (forfeited) — or, since
   * 2026-10-06, `'waived'`: returned BECAUSE it was the member's free late
   * change (the one waiver is now used). `null` otherwise. Read-only marker on
   * the Past tab.
   */
  readonly credit_outcome: "kept" | "returned" | "waived" | null;
  /**
   * #15 — the source that currently pays for this session, so the "Swap credit"
   * button can label it + exclude it as a target. `credit_source` is the kind;
   * exactly one of the two ids is set for package/membership/unlimited.
   */
  readonly credit_source: "package" | "membership" | "unlimited" | null;
  readonly member_package_id: string | null;
  readonly member_membership_id: string | null;
  /**
   * #15 swap-on-kept — raw `bookings.credit_restored`. `false` = credit still
   * held (swappable); `true` = returned; `null` = legacy/unknown. The Past-tab
   * "Swap credit" button is offered on a cancelled row ONLY when this is an
   * explicit `false` (parity with the backend guard).
   */
  readonly credit_restored: boolean | null;
  /** 2026-10-06 (first-late-cancel-waiver) — raw `bookings.late_change_waived`: `true` = this cancel or late move was the member's free first late change. */
  readonly late_change_waived: boolean | null;
}

export interface MemberDetailPayment {
  readonly id: string;
  readonly kind: string;
  readonly amount_minor: number;
  readonly currency: string;
  readonly status: string;
  readonly payment_method: string | null;
  readonly note: string | null;
  readonly occurred_at: string;
  /** For a refund row, the charge it reverses (`payments.id`); null otherwise. */
  readonly parent_payment_id: string | null;
}

export interface MemberDetailUpcomingPayment {
  readonly membership_id: string;
  readonly membership_slug: string;
  readonly lifecycle_state: string;
  readonly current_period_end: string;
  readonly remaining_sessions: number | null;
  /** The per-cycle amount this charge is expected at, or null when none resolved. */
  readonly per_cycle_minor: number | null;
  /** Charges left in the committed term, this one included; 1 for an open-ended plan. */
  readonly remaining_cycles: number;
  /** A fixed term's end, or null. */
  readonly term_ends_at: string | null;
}

export interface MemberDetailMembership {
  readonly id: string;
  readonly membership_slug: string;
  readonly lifecycle_state: string;
  readonly current_period_end: string | null;
  readonly remaining_sessions: number | null;
  /** Credits per cycle (the row's own, else the catalog's) — the "total" for the per-cycle "left / total". */
  readonly sessions_per_cycle: number | null;
  readonly status: string;
  readonly created_at: string;
  /** `metadata.paused === true` — the pause signal (lifecycle_state stays 'active'). */
  readonly paused: boolean;
  readonly pause_resumes_at: string | null;
  readonly pause_from: string | null;
  /** Whether the row carries a live Stripe subscription. */
  readonly is_stripe_backed: boolean;
  /** The row's own per-cycle count and price (a custom membership's sale), null when not stored. */
  readonly credits_per_cycle: number | null;
  readonly per_cycle_price_minor: number | null;
  /** The committed term in months, null when ongoing. */
  readonly term_months: number | null;
  /** The fixed term's end, null when ongoing. */
  readonly term_ends_at: string | null;
  /** The catalog session type the plan's credits book (`studio` / `home`), null when unknown. */
  readonly session_type: string | null;
  /**
   * membership-per-credit-revoke (2026-10-29) — one row per credit of the
   * balance, for the list behind the "Sessions (left / cycle)" cell; composed
   * server-side (`buildMembershipCreditRows`). Empty on an unlimited plan.
   */
  readonly credits: ReadonlyArray<MemberDetailMembershipCredit>;
  /** Credits taken off and later put back — history under the list. */
  readonly restored_credits: ReadonlyArray<MemberDetailRestoredCredit>;
  /** Where the list's "sessions since" starts, or null on an unlimited plan. */
  readonly cycle_started_at: string | null;
}

/**
 * per-credit-revoke (2026-10-29) — one row of a package's per-credit list,
 * mirroring `MemberDetailPackageCredit` in the backend member-detail
 * repository (which composes the rows; the SPA only renders them).
 */
export type MemberDetailPackageCredit =
  | {
      readonly kind: "used";
      readonly booking_id: string;
      readonly starts_at: string;
      readonly trainer_name: string | null;
      readonly status: string;
      readonly attendance_status: string | null;
      /** A cancel that kept the credit (late, or a staff "Use credit"). */
      readonly kept_on_cancel: boolean;
    }
  | {
      readonly kind: "transferred";
      readonly to_member_id: string | null;
      readonly to_member_name: string | null;
    }
  | {
      readonly kind: "revoked";
      readonly revocation_id: string;
      readonly revoked_at: string;
      readonly reason: string | null;
      readonly revoked_by_actor: string;
    }
  /** Consumed with no booking, transfer or revocation to show for it. */
  | { readonly kind: "adjusted" }
  | { readonly kind: "removed" }
  | { readonly kind: "available" };

/** A credit taken off and later put back — history under the list. */
export interface MemberDetailRestoredCredit {
  readonly revocation_id: string;
  readonly revoked_at: string;
  readonly reason: string | null;
  readonly restored_at: string;
  readonly restored_by_actor: string;
  readonly restore_note: string | null;
}

/**
 * membership-per-credit-revoke (2026-10-29) — one row of a membership's
 * per-credit list: the package kinds a running balance can show (no
 * `adjusted` or `removed`; `transferred` since the same day's
 * transfer-from-membership made a membership a transfer source), mirroring
 * the backend type.
 */
export type MemberDetailMembershipCredit = Extract<
  MemberDetailPackageCredit,
  { kind: "used" | "transferred" | "revoked" | "available" }
>;

export interface MemberDetailPackage {
  readonly id: string;
  readonly package_slug: string;
  readonly label: string;
  readonly paid_via: string | null;
  readonly remaining_sessions: number;
  /** Original package size — the "total" for the per-row "left / total". */
  readonly total_sessions: number | null;
  readonly expires_at: string | null;
  readonly source: string;
  readonly status: string;
  readonly granted_at: string;
  /**
   * Slice 3 — for a `source='transfer'` package, the member the credits came
   * FROM (id + display name). `null` for every other source.
   */
  readonly transfer_from_member_id: string | null;
  readonly transfer_from_member_name: string | null;
  /**
   * per-credit-revoke (2026-10-29) — one row per credit, for the list behind
   * the "Sessions (left / total)" cell; composed server-side.
   */
  readonly credits: ReadonlyArray<MemberDetailPackageCredit>;
  /** Credits taken off and later put back — history under the list. */
  readonly restored_credits: ReadonlyArray<MemberDetailRestoredCredit>;
  /** Consumption recorded beyond the pack size (a Codex opening balance). */
  readonly over_recorded_credits: number;
  /**
   * Migration 182 — book-now-pay-later. Minor units owed on this grant, `null`
   * when nothing was ever owed. An OPEN debt is `owed_minor !== null &&
   * settled_at === null`; once settled, `source` has already been rewritten to
   * manual / paid / comp, so `settled_at` is what distinguishes the two.
   */
  readonly owed_minor: number | null;
  readonly owed_currency: string | null;
  readonly settled_at: string | null;
  /**
   * HOW it settled — `record_payment` / `link_payment` / `waive` — `null`
   * while open. A waive is written OFF, not paid; the cell must say so.
   */
  readonly settled_as: string | null;
}

/** A reaction (emoji + when) the member placed on a message. */
export interface MemberDetailMessageReaction {
  readonly emoji: string;
  readonly at: string;
}

export interface MemberDetailMessage {
  readonly ts: string;
  readonly body: string | null;
  readonly dir: "in" | "out";
  /** Channel (`whatsapp` | `email` | `instagram`) — drives the bubble colour + chip. */
  readonly channel: string;
  /** 2026-06-29 — reactions on this message, rendered as an emoji badge. */
  readonly reactions: ReadonlyArray<MemberDetailMessageReaction>;
  /**
   * 2026-07-19 — the vendor's id for this message. For `channel === "email"`
   * it is the Gmail message id, which `read_gmail` takes; the bubble body is
   * only a `subject: …` stub, so this is the way back to the real email.
   * Null when the send predates id capture. Mirrors the server-side
   * `MemberDetailMessage` in `apps/dashboard/src/repositories/member-detail.ts`.
   */
  readonly vendor_message_id: string | null;
  /**
   * 2026-07-24 — outbound rows only: `outbound_messages.status`
   * (`pending`|`sent`|`delivered`|`read`|`failed`); `null` on inbound rows.
   * `failed` bubbles get a "Failed — not delivered" badge instead of
   * rendering like a delivered Studio message.
   */
  readonly status: string | null;
  /**
   * 2026-08-25 — outbound rows only: `outbound_messages.source` attribution.
   * `whatsapp_manual_send` / `instagram_manual_send` mark a HUMAN's app-typed
   * message ingested from its vendor echo (WhatsApp coexistence / Instagram
   * manual-send logging); those bubbles are labelled "Studio (app)" so staff
   * can tell who actually wrote a Studio message. `null` otherwise.
   */
  readonly source: string | null;
  /**
   * 2026-10-27 — inbound WhatsApp only: Meta's 131060 placeholder for a
   * contact's first message to a number onboarded from the WhatsApp Business
   * app. It arrived without text; the bubble says the words are in the Business
   * app (`emptyBubbleText`) instead of "(no text)".
   */
  readonly unavailable_first_message: boolean;
}

export interface MemberDetailConversation {
  readonly id: string;
  readonly channel: string;
  readonly status: string;
  readonly last_message_at: string | null;
}

/** Conversation channel including the migration-154 per-member AI switch. */
export interface MemberConversationChannel extends MemberDetailConversation {
  readonly ai_override: "on" | "off" | null;
}

/**
 * One AI pill's complete state (2026-08-06). The member page renders one per
 * AI-capable channel the member is REACHABLE on — a pill for a channel that
 * cannot carry a message would be noise, so the server omits it.
 *
 * `effective_on` is what the pill shows. `override` is this member's own
 * switch; `studio_mode` is what it falls back to when nobody has decided; and
 * `connector_enabled: false` disables the pill outright, because a paused
 * connector always has the final say — an individual "on" is not possible.
 */
export interface MemberChannelAi {
  readonly channel: "whatsapp" | "instagram" | "email";
  readonly label: string;
  readonly connector_enabled: boolean;
  readonly studio_mode: "notify" | "booking_intent" | "all";
  readonly override: "on" | "off" | null;
  readonly effective_on: boolean;
}

/**
 * `GET /api/members/:id/conversation` — the focused take-over payload behind the
 * MemberDetail Conversation panel (thread + the per-channel AI pills + the
 * three reply targets: phone / email / Instagram IGSID). Re-fetched after a
 * reply / pause toggle.
 */
export interface MemberConversationResponse {
  readonly member_id: string;
  readonly recipient_phone: string | null;
  readonly recipient_email: string | null;
  readonly recipient_igsid: string | null;
  readonly messages: ReadonlyArray<MemberDetailMessage>;
  readonly conversations: ReadonlyArray<MemberConversationChannel>;
  /** One entry per AI-capable channel the member is reachable on, in pill order. */
  readonly channel_ai: ReadonlyArray<MemberChannelAi>;
  readonly computed_at: string;
}

/**
 * `instagram_dm_threads` router state, on the OUTREACH profile only — 'notify'
 * = the AI never replies; 'agent' = the booking agent replies autonomously
 * inside the 24h window. Flipped via `set_instagram_thread_mode` from the
 * `/pr/:id` page.
 *
 * Deliberately absent from the member payload since 2026-08-06: a member's
 * per-channel AI switch is `channel_ai` (backed by `conversations.ai_override`,
 * which is tri-state and can say "nobody decided"). Thread mode survives for
 * outreach contacts precisely because they have no member row to hang an
 * override on.
 */
export interface InstagramThreadState {
  readonly igsid: string;
  readonly mode: "notify" | "agent";
}

export interface MemberDetailFlow {
  readonly id: string;
  readonly flow_name: string;
  readonly status: string;
  readonly current_step_index: number;
  readonly started_at: string;
  readonly last_step_at: string | null;
  readonly completed_at: string | null;
}

export interface MemberDetailSessionNote {
  readonly id: string;
  readonly booking_id: string | null;
  readonly trainer_id: string | null;
  readonly trainer_name: string | null;
  readonly body: string;
  readonly structured: Record<string, unknown>;
  readonly created_by_actor: string;
  readonly created_at: string;
}

export interface MemberDetailTag {
  readonly tag_id: string;
  readonly slug: string;
  readonly label: string;
  readonly added_at: string;
}

export interface MemberDetailMemory {
  readonly claude_md: string;
  readonly summary_md: string;
  readonly rolling_md: string;
  readonly longterm_md: string;
  readonly rolling_day_count: number;
  readonly last_entry_on: string | null;
  readonly last_recycled_at: string | null;
  readonly updated_at: string;
}

export interface MemberDetailJourneyEvent {
  readonly ts: string;
  readonly kind: string;
  readonly label: string;
  readonly detail: string | null;
  readonly actor: string;
}

/** GET /api/dashboard-layout — the staff user's saved layout for a page. The
 *  `preferences` blob is the same shape `save_dashboard_layout` persists (card
 *  order / span / height / hidden / journeyKinds); `null` when never saved. */
export interface DashboardLayoutResponse {
  readonly preferences: Record<string, unknown> | null;
}

/**
 * 2026-10-06 (first-late-cancel-waiver) — the member's standing for the
 * studio's free first late change (the backend's `LateChangeWaiverAvailability`,
 * read with the same code the cancel saga decides by). `configured` false =
 * the studio has no `late_change_waiver` block, and the page shows nothing.
 */
export interface MemberLateChangeWaiver {
  readonly configured: boolean;
  readonly stage: "lead" | "trial" | "customer" | null;
  readonly eligible: boolean;
  readonly free: number;
  readonly used: number;
  readonly available: boolean;
}

// --- WhatsApp picture intake (2026-10-14) ------------------------------------
// Mirrors apps/dashboard/src/repositories/picture-intakes.ts. A form's answers
// are a member's health data — staff-facing, as on the member page.

/** One consultation form photographed on WhatsApp about this member. */
export interface MemberDetailConsultationForm {
  readonly id: string;
  /** `picture_intakes.status`. */
  readonly status: string;
  readonly sender_kind: "member" | "staff";
  readonly sender_staff_name: string | null;
  readonly received_at: string;
  readonly prompt_sent_at: string | null;
  readonly ended_at: string | null;
  readonly ended_by: {
    readonly kind: "member" | "staff" | "cli" | "other";
    readonly via: "whatsapp" | "dashboard" | "other" | null;
    readonly staff_name: string | null;
  } | null;
  readonly expires_at: string;
  readonly failed_reason: string | null;
  readonly has_photo: boolean;
}

export interface WaitingFormAnswer {
  readonly key: string;
  readonly label: string;
  readonly value: string;
}

export interface WaitingFormCandidate {
  readonly member_id: string;
  readonly display_name: string | null;
  readonly reasons: ReadonlyArray<string>;
}

/** GET /api/members/picture-intakes/waiting — one form waiting for a member. */
export interface WaitingPictureIntakeForm {
  readonly id: string;
  readonly received_at: string;
  readonly expires_at: string;
  readonly sender_staff_name: string | null;
  readonly form_name: string | null;
  readonly answers: ReadonlyArray<WaitingFormAnswer>;
  readonly notes_for_review: string | null;
  readonly candidates: ReadonlyArray<WaitingFormCandidate>;
  readonly has_photo: boolean;
}

export interface WaitingPictureIntakesResponse {
  readonly forms: ReadonlyArray<WaitingPictureIntakeForm>;
  readonly has_more: boolean;
  readonly computed_at: string;
}

/** GET /api/members/picture-intakes/:id/photo — a five-minute signed URL. */
export interface PictureIntakePhotoResponse {
  readonly ok: true;
  readonly url: string;
  readonly expires_at: string;
}

export interface MemberDetailResponse {
  readonly member: MemberDetailAccount;
  readonly upcoming_appointments: ReadonlyArray<MemberDetailAppointment>;
  readonly past_appointments: ReadonlyArray<MemberDetailAppointment>;
  readonly upcoming_payments: ReadonlyArray<MemberDetailUpcomingPayment>;
  readonly past_payments: ReadonlyArray<MemberDetailPayment>;
  readonly late_change_waiver: MemberLateChangeWaiver | null;
  readonly memberships: ReadonlyArray<MemberDetailMembership>;
  readonly packages: ReadonlyArray<MemberDetailPackage>;
  readonly messages: ReadonlyArray<MemberDetailMessage>;
  readonly conversations: ReadonlyArray<MemberDetailConversation>;
  readonly flows: ReadonlyArray<MemberDetailFlow>;
  readonly tags: ReadonlyArray<MemberDetailTag>;
  readonly session_notes: ReadonlyArray<MemberDetailSessionNote>;
  /** Consultation forms photographed on WhatsApp about this member, newest first. */
  readonly consultation_forms: ReadonlyArray<MemberDetailConsultationForm>;
  readonly memory: MemberDetailMemory | null;
  readonly journey: ReadonlyArray<MemberDetailJourneyEvent>;
  readonly computed_at: string;
}

// --- Outreach contact profile (GET /api/outreach/:id) ------------------------
// Mirrors the server's outreach-detail.ts shapes. Contact name/email/phone +
// message bodies are PII (staff-facing, same as the member page).

export interface OutreachDetailContact {
  readonly id: string;
  readonly type: string;
  readonly name: string;
  readonly gender: string | null;
  readonly age: number | null;
  readonly occupation: string | null;
  readonly company_outlet: string | null;
  readonly location: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly instagram: string | null;
  readonly linkedin: string | null;
  readonly other_socials: Record<string, unknown>;
  readonly best_contact_method: string | null;
  readonly audience_size: number | null;
  readonly niche: string | null;
  readonly quality_score: number | null;
  readonly quality_notes: string | null;
  readonly suggested_angle: string | null;
  readonly status: string;
  readonly contact_date: string | null;
  readonly channel_used: string | null;
  readonly message_summary: string | null;
  readonly response_date: string | null;
  readonly response: string | null;
  readonly next_action: string | null;
  /** Operator's standing comment — fed to Peter on every list (mig 132). */
  readonly staff_notes: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly last_email_event: LastEmailEvent | null;
}

/** Per-message Resend rollup for an outbound hello@ send, keyed by the thread
 *  row's `vendor_message_id`. Gmail sends have no entry (no tracking exists). */
export interface OutreachEmailTracking {
  readonly delivered_at: string | null;
  readonly opened_at: string | null;
  readonly clicked_at: string | null;
  readonly bounced_at: string | null;
  readonly complained_at: string | null;
  readonly opened_count: number;
  readonly clicked_count: number;
}

/** GET /api/outreach/:id — the contact profile: header + full cross-channel
 *  thread + the three reply targets (phone / email / IGSID). */
export interface OutreachDetailResponse {
  readonly contact: OutreachDetailContact;
  readonly messages: ReadonlyArray<MemberDetailMessage>;
  readonly email_tracking: Readonly<Record<string, OutreachEmailTracking>>;
  readonly recipient_phone: string | null;
  readonly recipient_email: string | null;
  readonly recipient_igsid: string | null;
  /** Per-contact "Instagram AI" chip state — null when no thread exists. */
  readonly instagram_thread: InstagramThreadState | null;
  readonly computed_at: string;
}

// --- PR board (GET /api/pr-board) --------------------------------------------
// Mirrors the server's pr-board.ts shapes (Peter's PR pipeline, migration 126).
// Two lifecycles, deliberately separate: an ENGAGEMENT is one collaboration
// attempt and has a stage; a PERSON is an `outreach_targets` row and has a
// contact status. Superseded the `/outreach` board, which only had the latter.

/** Pipeline stage — declaration order IS pipeline order (mirrors the PG enum). */
export type PrEngagementStage =
  | "pitching"
  | "pitched"
  | "replied"
  | "agreed"
  | "prepped"
  | "session_done"
  | "delivered"
  | "closed";

export type PrExchangeKind =
  | "publication"
  | "social_content"
  | "paying_plus_one"
  | "partnership";

/** One collaboration, with its full per-stage timestamp strip. */
export interface PrEngagement {
  readonly pr_engagement_id: string;
  readonly outreach_target_id: string;
  // NOT nullable, deliberately. `list_pr_engagements` INNER JOINs
  // `outreach_targets` and migration 157 did not touch it — a PR engagement
  // always has an outreach target, and its type is never 'member'. These were
  // briefly widened alongside `InboxThread` below, which forced a
  // `?? "counterparty"` fallback in Pr.tsx that would have badged a journalist
  // as a counterparty for a null that cannot occur.
  readonly target_name: string;
  readonly target_type: string;
  readonly target_company_outlet: string | null;
  /** Non-null ⇒ they are already a member (never cold-pitch). */
  readonly target_member_id: string | null;
  readonly stage: PrEngagementStage;
  readonly angle: string;
  readonly exchange_kind: PrExchangeKind;
  readonly session_location: string | null;
  readonly pitched_at: string | null;
  readonly replied_at: string | null;
  readonly agreed_at: string | null;
  readonly prepped_at: string | null;
  readonly session_at: string | null;
  readonly followed_up_at: string | null;
  readonly delivered_at: string | null;
  readonly closed_at: string | null;
  readonly closed_reason: string | null;
  readonly next_action_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly last_event: {
    readonly at: string;
    readonly kind: string;
    readonly summary: string;
  } | null;
  readonly timeline_count: number;
}

/**
 * The overview strip. Studio-wide and filter-independent on purpose, and each
 * counter is derived from a TIMESTAMP rather than the current stage — so someone
 * who replied and later closed still counts as replied.
 */
export interface PrEngagementCounts {
  readonly total: number;
  readonly contacted: number;
  readonly replied: number;
  readonly agreed: number;
  readonly sessions_done: number;
  readonly delivered: number;
  readonly closed: number;
  /** Delivered collaborations with a `local_partner` — "started a partnership". */
  readonly partnerships: number;
  readonly by_stage: Readonly<Record<PrEngagementStage, number>>;
  readonly delivered_by_exchange_kind: Readonly<Record<PrExchangeKind, number>>;
}

/** One PR contact — journalist / influencer / local partner. */
export interface PrBoardPerson {
  readonly id: string;
  readonly type: string;
  readonly name: string;
  readonly company_outlet: string | null;
  readonly niche: string | null;
  readonly location: string | null;
  readonly audience_size: number | null;
  readonly quality_score: number | null;
  readonly status: string;
  readonly email: string | null;
  readonly best_contact_method: string | null;
  readonly contact_date: string | null;
  readonly channel_used: string | null;
  readonly response_date: string | null;
  readonly next_action: string | null;
  readonly suggested_angle: string | null;
  /** Non-null ⇒ already a member. Shown as a badge so nobody cold-pitches them. */
  readonly member_id: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly last_email_event: LastEmailEvent | null;
  readonly live_engagement: {
    readonly id: string;
    readonly stage: PrEngagementStage;
    readonly exchange_kind: string;
  } | null;
}

export interface PrBoardStageGroup {
  readonly stage: PrEngagementStage;
  readonly engagements: ReadonlyArray<PrEngagement>;
}

/** GET /api/pr-board — the `/pr` page in one payload. */
export interface PrBoardResponse {
  readonly groups: ReadonlyArray<PrBoardStageGroup>;
  readonly counts: PrEngagementCounts;
  readonly people: ReadonlyArray<PrBoardPerson>;
  /** True when engagements were truncated (only ever closed ones — see server). */
  readonly has_more: boolean;
  /**
   * The Peter drain's latest heartbeat, for the approvals panel's Approve gate.
   * OPTIONAL on the wire: a dashboard build older than 2026-08-08 omits it, and
   * `approvalLiveness(null)` treats an absent beat as "never reported", which
   * disables Approve — fail closed, not open.
   */
  readonly drain?: DrainLivenessWire;
  readonly computed_at: string;
}

/**
 * A drain's latest heartbeat row (`status_self_test_results`), as both board
 * endpoints return it. `paused` and `stale` are DERIVED server-side so the
 * freshness rule stays single-sourced with `/status`'s capability window.
 */
export interface DrainLivenessWire {
  readonly capability_id: string;
  /** `null` when the drain has never beaten at all. */
  readonly ok: boolean | null;
  readonly tested_at: string | null;
  readonly detail: string | null;
  readonly paused: boolean;
  readonly stale: boolean;
}

// --- Inbox board (GET /api/inbox-board + /api/inbox/threads/:id) -------------
// Mirrors the server's `inbox-board.ts` shapes — the operator-mail lane.

export type InboxThreadStage =
  | "unclassified"
  | "triaged"
  | "drafting"
  | "awaiting_mario"
  | "revising"
  | "replied"
  | "awaiting_them"
  | "needs_mario"
  | "closed";

export interface InboxThread {
  readonly inbox_thread_id: string;
  /**
   * NULL for a MEMBER matter. Migration 157 made the column nullable and the
   * `inbox_threads_exactly_one_person` CHECK guarantees exactly one of this
   * and `member_id` is set — so a reader branches on which is non-null.
   *
   * Was typed non-nullable until 2026-08-07, which made `/inbox/threads/:id`
   * fetch `/api/outreach/null` for every member matter (silently dropping the
   * person grid and the conversation) and render a dead `/pr/people/null` link.
   */
  readonly outreach_target_id: string | null;
  /** Null only if the person row vanished under a race (migration 157). */
  readonly target_name: string | null;
  /** 'member' for a member matter, else the outreach target's type. */
  readonly target_type: string | null;
  /** Set for a MEMBER matter — notify-only, never draftable (guard I3). */
  readonly member_id: string | null;
  readonly target_email: string | null;
  /** OUR receiving address — the tab key. */
  readonly mailbox_identifier: string;
  readonly gmail_thread_id: string;
  readonly stage: InboxThreadStage;
  readonly category: string | null;
  readonly urgency: "low" | "normal" | "high" | null;
  readonly subject: string | null;
  readonly summary: string | null;
  readonly classifier_rationale: string | null;
  readonly next_action: string | null;
  readonly next_action_at: string | null;
  readonly notify_pending: boolean;
  readonly revision_round: number;
  readonly triaged_at: string | null;
  readonly drafting_at: string | null;
  readonly awaiting_mario_at: string | null;
  readonly revising_at: string | null;
  readonly replied_at: string | null;
  readonly awaiting_them_at: string | null;
  readonly needs_mario_at: string | null;
  readonly closed_at: string | null;
  readonly closed_reason: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly last_event: {
    readonly at: string;
    readonly kind: string;
    readonly summary: string;
  } | null;
  readonly timeline_count: number;
}

export interface InboxThreadCounts {
  readonly total: number;
  readonly open: number;
  readonly notify_pending: number;
  readonly awaiting_mario: number;
  readonly needs_mario: number;
  /** Stamp-derived — a thread that replied and moved on still counts. */
  readonly replied: number;
  readonly closed: number;
  readonly by_stage: Readonly<Record<InboxThreadStage, number>>;
  /** Open threads per lowercased receiving address — the tab strip. */
  readonly by_mailbox: Readonly<Record<string, number>>;
}

export interface InboxBoardCorrespondent {
  readonly id: string;
  readonly type: string;
  readonly name: string;
  readonly email: string | null;
  readonly company_outlet: string | null;
  readonly status: string;
  readonly contact_date: string | null;
  readonly response_date: string | null;
  readonly open_threads: number;
  readonly total_threads: number;
  readonly latest_thread_id: string | null;
  readonly latest_thread_stage: InboxThreadStage | null;
}

export interface InboxBoardStageGroup {
  readonly stage: InboxThreadStage;
  readonly threads: ReadonlyArray<InboxThread>;
}

/** GET /api/inbox-board — the `/inbox` page in one payload. */
export interface InboxBoardResponse {
  readonly groups: ReadonlyArray<InboxBoardStageGroup>;
  readonly counts: InboxThreadCounts;
  readonly correspondents: ReadonlyArray<InboxBoardCorrespondent>;
  /**
   * 2026-08-07 — the studio's declared `scan: true` mailbox addresses,
   * lowercased + sorted. The tab strip unions these with
   * `counts.by_mailbox`, so a scanned mailbox with no open matters shows a
   * `(0)` tab instead of disappearing. OPTIONAL on the wire: a dashboard
   * deployed before this field simply omits it.
   */
  readonly declared_mailboxes?: ReadonlyArray<string>;
  readonly has_more: boolean;
  /** The inbox drain's latest heartbeat — see {@link DrainLivenessWire}. */
  readonly drain?: DrainLivenessWire;
  readonly computed_at: string;
}

export interface InboxTimelineEntry {
  readonly id: string;
  readonly at: string;
  readonly kind: string;
  readonly stage: InboxThreadStage | null;
  readonly summary: string;
  /**
   * Optional long-form reasoning (migration 163, cluster A). `summary` is the
   * list line; this is what the expanded entry shows. Null on entries written
   * before 2026-08-09 and on any that needed only one clause.
   */
  readonly detail: string | null;
  /** Mario's VERBATIM rejection text (slice 7) — the one verbatim column. */
  readonly feedback: string | null;
  readonly resolves_id: string | null;
  readonly actor: string;
}

// --- Decisions board (GET /api/decisions-board + /api/decisions/:id) ---------
// Mirrors the server's `decisions-board.ts` shapes — the multi-admin voting
// lane (migration 174). Enum vocabularies are append-only in Postgres; every
// consumer falls back to the raw value for a member it hasn't shipped a
// label for.

export type StaffDecisionKind = "email_correspondent" | "ad_hoc" | "trainer_review_call";
export type StaffDecisionQuorum = "unanimous" | "majority";
export type StaffDecisionStatus = "open" | "resolved" | "cancelled";

/** One option on a decision (an entry of the `options` jsonb array). */
export interface DecisionOption {
  readonly label: string;
  /** Canonical action staged behind this option, or null for decide-only. */
  readonly action: string | null;
  readonly action_input: unknown;
}

/** One admin's ballot in one round. Votes are OPEN to both admins by design. */
export interface DecisionBallot {
  readonly id: string;
  readonly staff_id: string;
  readonly display_name: string;
  /** The voter's registry email — how the SPA claims the viewer's ballot. */
  readonly email: string | null;
  readonly option_index: number | null;
  readonly voted_at: string | null;
  /** 'telegram' | 'whatsapp' | 'dashboard', null while unvoted. */
  readonly voted_via: string | null;
  /**
   * The ballot's bearer token — non-null ONLY on the signed-in viewer's own
   * ballot (server-scoped against the JWT email). Feeds `cast_staff_vote`.
   */
  readonly token: string | null;
}

export interface DecisionRound {
  readonly round: number;
  readonly ballots: ReadonlyArray<DecisionBallot>;
}

export interface StaffDecision {
  readonly id: string;
  readonly kind: StaffDecisionKind;
  readonly title: string;
  readonly context: string | null;
  readonly options: ReadonlyArray<DecisionOption>;
  readonly quorum: StaffDecisionQuorum;
  readonly status: StaffDecisionStatus;
  /** The CURRENT round number; `rounds` carries the full history. */
  readonly round: number;
  readonly winning_option_index: number | null;
  readonly resolved_at: string | null;
  /** Resolving staff_id — mapped to a name via the ballots client-side. */
  readonly resolved_by: string | null;
  readonly dispatched_action: string | null;
  readonly dispatched_run_ref: string | null;
  /**
   * Migration 221 — set together when the winning option's action failed on
   * every attempt; `dispatched_action` then stays null. Reason:
   * `terminal` | `retries_exhausted` | `outcome_unknown` (may have run).
   */
  readonly dispatch_failed_at: string | null;
  readonly dispatch_error_code: string | null;
  readonly dispatch_failure_reason: string | null;
  readonly source_ref: string | null;
  readonly last_ballot_sent_at: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  /** Every round ascending — the current one is `round === decision.round`. */
  readonly rounds: ReadonlyArray<DecisionRound>;
}

export interface DecisionsBoardCounts {
  readonly total: number;
  readonly open: number;
  readonly resolved: number;
  readonly cancelled: number;
}

/** GET /api/decisions-board — the `/decisions` page in one payload. */
export interface DecisionsBoardResponse {
  /** Open decisions, newest first. */
  readonly open: ReadonlyArray<StaffDecision>;
  /** Resolved + cancelled decisions, most recently updated first. */
  readonly recent: ReadonlyArray<StaffDecision>;
  readonly counts: DecisionsBoardCounts;
  /** True when either list was truncated at its fetch limit. */
  readonly has_more: boolean;
  readonly computed_at: string;
}

// ---------------------------------------------------------------------------
// /inbox — the WhatsApp + Instagram channel tabs (READ-ONLY, 2026-08-06).
//
// Keyed on the CONVERSATION, not on a person: most Instagram senders match no
// member and no PR contact, and before this they were visible only as raw rows
// in /data/inbound_messages.
// ---------------------------------------------------------------------------

export type ChatBoardChannel = "whatsapp" | "instagram";

export interface ChatBoardConversation {
  readonly channel: string;
  /** E.164 (WhatsApp) or IGSID/@handle (Instagram) — the durable routing key. */
  readonly sender_identifier: string;
  /** Member name › PR contact name › @handle › the raw identifier. */
  readonly display_name: string;
  readonly member_id: string | null;
  readonly outreach_target_id: string | null;
  readonly ig_username: string | null;
  /** Instagram only — when the 24h customer-service reply window shuts. */
  readonly window_expires_at: string | null;
  /** `on` | `off` | null (inherit the studio's autonomy setting). */
  readonly ai_override: string | null;
  readonly last_at: string;
  readonly last_direction: "in" | "out";
  readonly awaiting_reply: boolean;
  readonly message_count: number;
}

export interface ChatBoardCounts {
  readonly total: number;
  readonly awaiting_reply: number;
  readonly by_channel: Readonly<Record<string, number>>;
  readonly awaiting_by_channel: Readonly<Record<string, number>>;
}

/** GET /api/inbox-chats — every WhatsApp + Instagram conversation. */
/**
 * `GET /api/instagram-comments` — the /inbox Instagram comments tab.
 *
 * `author_username` is NULLABLE and that is a REAL case: Meta withheld the
 * comment author by SILENT FIELD OMISSION until the scope was granted
 * 2026-08-07. Render `handle unavailable`, never a made-up name — and
 * `counts.authorless` is what makes a fully-anonymous board loud.
 */
export interface InstagramCommentRow {
  readonly id: string;
  readonly kind: "comment" | "live_comment" | "mention";
  readonly author_username: string | null;
  readonly text: string | null;
  readonly occurred_at: string;
  /** true when `occurred_at` is our ingest clock, not the comment's own time. */
  readonly time_is_approximate: boolean;
  /** DURABLE. Prefer over the thumbnail, whose CDN URL expires. */
  readonly media_permalink: string | null;
  readonly media_thumbnail_url: string | null;
  readonly replied_at: string | null;
  readonly reply_comment_id: string | null;
  readonly enrichment_error: string | null;
  /** Funnel DMs staged against this comment (2026-09-24). Usually 0 or 1. */
  readonly funnel_sends: ReadonlyArray<InstagramFunnelStagedSend>;
}

/**
 * A staged funnel DM, as the `/inbox` approval block renders it.
 *
 * ⚠️ There is NO `author_igsid` here and there must not be. The server does not
 * select it: approve and skip are addressed by `id`, so the commenter's
 * Instagram-scoped id never needs to reach a browser.
 */
export interface InstagramFunnelStagedSend {
  readonly id: string;
  readonly campaign_name: string;
  readonly status: "staged" | "sent" | "skipped" | "expired" | "failed";
  readonly staged_body: string;
  readonly chip_label: string;
  readonly public_reply_body: string | null;
  readonly private_reply_expires_at: string;
  readonly sent_at: string | null;
  readonly error_code: string | null;
  readonly mode: "approve" | "auto";
}

export interface InstagramCommentsResponse {
  readonly counts: {
    readonly total: number;
    readonly awaiting_reply: number;
    readonly authorless: number;
    readonly awaiting_funnel_approval: number;
  };
  readonly comments: ReadonlyArray<InstagramCommentRow>;
}

export interface ChatsBoardResponse {
  readonly conversations: ReadonlyArray<ChatBoardConversation>;
  readonly counts: ChatBoardCounts;
  readonly has_more: boolean;
}

/** GET /api/inbox-chats/:channel/:sender — one conversation, read-only. */
export interface ChatThreadResponse {
  readonly channel: string;
  readonly sender_identifier: string;
  readonly display_name: string;
  readonly member_id: string | null;
  readonly outreach_target_id: string | null;
  readonly messages: ReadonlyArray<MemberDetailMessage>;
}

// ---------------------------------------------------------------------------
// /rota — the trainer-ops rota board (migration 149).
// ---------------------------------------------------------------------------

export type RotaSubmissionStage =
  | "requested"
  | "reminded"
  | "submitted"
  | "confirmed"
  | "staged"
  | "uploaded"
  | "escalated"
  | "skipped";

export interface RotaCycle {
  readonly rota_cycle_id: string;
  /** `YYYY-MM-DD` (always the 1st of the rota month). */
  readonly month: string;
  readonly status: "collecting" | "complete" | "closed";
  readonly opens_on: string;
  readonly remind1_on: string;
  readonly remind2_on: string;
  readonly escalate_on: string;
  readonly deadline_on: string;
  readonly coverage_ok: boolean | null;
  readonly coverage_checked_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface RotaSubmission {
  readonly rota_submission_id: string;
  readonly rota_cycle_id: string;
  readonly trainer_id: string;
  readonly trainer_display_name: string;
  readonly trainer_telegram_bound: boolean;
  readonly stage: RotaSubmissionStage;
  readonly windows: Record<string, unknown> | null;
  readonly reminder_count: number;
  readonly summary: string | null;
  readonly reminded_at: string | null;
  readonly submitted_at: string | null;
  readonly confirmed_at: string | null;
  readonly staged_at: string | null;
  readonly uploaded_at: string | null;
  readonly escalated_at: string | null;
  readonly skipped_at: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  /** Bookable slots this trainer already has inside the cycle month. */
  readonly slots_in_month: number;
}

export interface RotaSubmissionCounts {
  readonly total: number;
  readonly outstanding: number;
  readonly uploaded: number;
  readonly escalated: number;
  readonly by_stage: Readonly<Record<RotaSubmissionStage, number>>;
}

export interface RotaSubmissionProposal {
  readonly pending_action_id: string;
  readonly status: string;
  readonly created_at: string;
}

export interface RotaTimelineEntry {
  readonly id: string;
  readonly at: string;
  readonly kind: string;
  readonly stage: RotaSubmissionStage | null;
  readonly summary: string;
  /**
   * Optional long-form reasoning (migration 163, cluster A). `summary` is the
   * list line; this is what the expanded entry shows. Null on entries written
   * before 2026-08-09 and on any that needed only one clause.
   */
  readonly detail: string | null;
  readonly actor: string;
  readonly rota_submission_id: string | null;
}

/** GET /api/rota-board — the `/rota` page in one payload. */
export interface RotaBoardResponse {
  readonly cycle: RotaCycle | null;
  readonly submissions: ReadonlyArray<RotaSubmission>;
  readonly counts: RotaSubmissionCounts;
  readonly months: ReadonlyArray<string>;
  readonly proposals: Readonly<Record<string, RotaSubmissionProposal>>;
  readonly timeline: ReadonlyArray<RotaTimelineEntry>;
  readonly computed_at: string;
}

// ---------------------------------------------------------------------------
// /payroll — the trainer payroll board (migration 152).
// ---------------------------------------------------------------------------

export interface PayrollRunSummary {
  readonly payroll_run_id: string;
  readonly period_month: string;
  readonly status: "draft" | "in_review" | "approved" | "delivered";
  readonly computed_at: string | null;
  readonly totals: Record<string, {
    readonly total_minor: number;
    readonly by_component: Record<string, number>;
    readonly payable_sessions: number;
    readonly no_show_sessions: number;
  }>;
  readonly data_gaps: ReadonlyArray<{ kind: string; detail: string; refs: string[] }>;
  readonly approved_by: string | null;
  readonly approved_at: string | null;
  readonly delivered_at: string | null;
  readonly created_at: string;
}

export interface PayrollBoardLine {
  readonly trainer_id: string;
  readonly trainer_display_name: string;
  readonly component: string;
  readonly quantity: number;
  readonly rate: Record<string, unknown>;
  readonly amount_minor: number;
  readonly currency: string;
  readonly evidence: ReadonlyArray<string>;
}

export interface PayrollBoardAdjustment {
  readonly id: string;
  readonly trainer_id: string;
  readonly trainer_display_name: string;
  readonly amount_minor: number;
  readonly reason: string;
  readonly created_by: string;
  readonly created_at: string;
}

/** GET /api/payroll-board — the `/payroll` page in one payload. */
export interface PayrollBoardResponse {
  readonly runs: ReadonlyArray<PayrollRunSummary>;
  readonly selected: PayrollRunSummary | null;
  readonly lines: ReadonlyArray<PayrollBoardLine>;
  readonly adjustments: ReadonlyArray<PayrollBoardAdjustment>;
  readonly computed_at: string;
}

// ---------------------------------------------------------------------------
// Trainer performance — the wire shape of `buildTrainerPerformanceReport`
// (`packages/canonical-actions/src/trainer-performance/types.ts`), mirrored
// here because the SPA tree does not import the canonical packages.
// ---------------------------------------------------------------------------

export type PromptOutcomeTier =
  | "captured"
  | "captured_unkeyed"
  | "captured_profile"
  | "replied_other_member"
  | "replied_no_capture"
  | "silent";

export interface TrainerPerformanceKpis {
  readonly sessions: {
    readonly delivered: number;
    readonly hours: number;
    readonly no_shows: number;
    readonly late_cancels: number;
  };
  readonly occupancy: {
    readonly slots: number;
    readonly capacity: number;
    readonly booked: number;
    readonly pct: number | null;
  };
  readonly clients: {
    readonly active: number;
    readonly new_to_trainer: number;
    readonly last_month: number;
    readonly retained_with_trainer: number;
    readonly retained_with_anyone: number;
    readonly retention_pct: number | null;
  };
  readonly ratings: {
    readonly count: number;
    readonly average: number | null;
    readonly poor: number;
    readonly excellent: number;
  };
  readonly sales: {
    readonly trials: number;
    readonly trials_signed_up: number;
    readonly trial_conversion_pct: number | null;
    readonly trials_3m: number;
    readonly trials_signed_up_3m: number;
    readonly trial_conversion_pct_3m: number | null;
    readonly signups: number;
    readonly repeat_purchases: number;
  };
  readonly sops: {
    readonly postsession: {
      readonly graded: number;
      readonly compliant: number;
      readonly pct: number | null;
      readonly by_tier: Readonly<Record<PromptOutcomeTier, number>>;
    };
    readonly consultation: {
      readonly due: number;
      readonly on_file: number;
      readonly pct: number | null;
      readonly not_yet_due: number;
    };
  };
  readonly pay: {
    readonly total_minor: number;
    readonly by_component: Readonly<Record<string, number>>;
    readonly bonus_minor: number;
    readonly revenue_minor: number | null;
    readonly pay_to_revenue_pct: number | null;
    readonly unpriced_sessions: number;
    readonly currency: string;
  } | null;
}

export interface TrainerPerformancePurchase {
  readonly member_id: string;
  readonly purchase_id: string;
  readonly kind: "package" | "membership";
  readonly slug: string;
  readonly purchased_at: string;
}

export interface TrainerPerformanceEvidence {
  readonly consultations_missing: ReadonlyArray<{
    readonly member_id: string;
    readonly booking_id: string;
    readonly session_ended_at: string;
  }>;
  readonly postsession_misses: ReadonlyArray<{
    readonly booking_id: string;
    readonly member_id: string;
    readonly session_ended_at: string;
    readonly tier: PromptOutcomeTier;
  }>;
  readonly trials_not_signed_up: ReadonlyArray<{
    readonly member_id: string;
    readonly booking_id: string;
    readonly session_ended_at: string;
  }>;
  readonly signups: ReadonlyArray<TrainerPerformancePurchase>;
  readonly repeat_purchases: ReadonlyArray<TrainerPerformancePurchase>;
  readonly rating_comments: ReadonlyArray<{
    readonly feedback_id: string;
    readonly member_id: string;
    readonly booking_id: string | null;
    readonly rating: number;
    readonly comment: string | null;
    readonly rated_at: string;
  }>;
}

export interface TrainerPerformanceEntry {
  readonly trainer_id: string;
  readonly display_name: string | null;
  readonly status: string;
  readonly admin_linked: boolean;
  readonly kpis: TrainerPerformanceKpis;
  readonly previous: TrainerPerformanceKpis;
  readonly headline: TrainerPerformanceHeadline;
  readonly previous_headline: TrainerPerformanceHeadline;
  readonly evidence: TrainerPerformanceEvidence;
}

export type TrainerPerformanceHeadlineKey =
  | "delivered"
  | "hours"
  | "occupancy_pct"
  | "active_clients"
  | "new_clients"
  | "retention_pct"
  | "rating_average"
  | "rating_count"
  | "trials"
  | "trial_conversion_pct_3m"
  | "signups"
  | "repeat_purchases"
  | "postsession_pct"
  | "consultation_pct"
  | "pay_total_minor"
  | "bonus_minor"
  | "revenue_minor"
  | "pay_to_revenue_pct"
  | "late_cancels"
  | "no_shows";

export type TrainerPerformanceHeadline = Readonly<
  Record<TrainerPerformanceHeadlineKey, number | null>
>;

export interface TrainerPerformancePayMeta {
  readonly source: "payroll_run" | "provisional" | "unavailable";
  readonly payroll_run: {
    readonly payroll_run_id: string;
    readonly status: string;
    readonly computed_at: string | null;
  } | null;
}

export interface TrainerPerformanceReport {
  readonly month: string;
  readonly previous_month: string;
  readonly period: {
    readonly starts_at: string;
    readonly ends_at: string;
    readonly timezone: string;
  };
  readonly is_complete_month: boolean;
  readonly computed_at: string;
  readonly pay: TrainerPerformancePayMeta;
  readonly previous_pay: TrainerPerformancePayMeta;
  readonly team: {
    readonly median: TrainerPerformanceHeadline;
    readonly previous_median: TrainerPerformanceHeadline;
    readonly trainers_in_median: number;
    readonly unattributed_repeat_purchases: number;
  };
  readonly trainers: ReadonlyArray<TrainerPerformanceEntry>;
  readonly definitions: {
    readonly reply_window_hours: number;
    readonly consultation_grace_hours: number;
    readonly compliance_epoch: string;
    readonly repeat_purchase_lookback_days: number;
  };
}

/** GET /api/trainer-performance — the `/trainer-performance` page in one payload. */
export interface TrainerPerformanceResponse {
  readonly report: TrainerPerformanceReport;
  /** member id → display name, for every member the evidence names. */
  readonly member_names: Readonly<Record<string, string>>;
}

/**
 * How far a review's WhatsApp copy got (trainer-review-lane, 2026-10-23),
 * derived by the server from the held row and Meta's status callbacks.
 */
export type TrainerReviewWhatsAppStatus =
  | "pending"
  | "waiting_for_continue"
  | "expired"
  | "not_possible"
  | "failed"
  | "sent"
  | "delivered"
  | "read";

/** One trainer's monthly review — the "Monthly review" panel's row. */
export interface TrainerReviewSummary {
  readonly review_id: string;
  readonly trainer_id: string;
  readonly status: "pending" | "sent" | "failed" | "skipped";
  readonly skip_reason: string | null;
  readonly has_scorecard: boolean;
  readonly sent_at: string | null;
  readonly whatsapp_status: TrainerReviewWhatsAppStatus | null;
  readonly questions_sent: number;
  readonly email_state: "pending" | "sent" | "failed" | null;
  readonly last_error: string | null;
  readonly answers: {
    readonly q1_rating: number | null;
    readonly q2_best: string | null;
    readonly q3_obstacle: string | null;
    readonly q4_topic: string | null;
  };
  readonly answers_updated_at: string | null;
  readonly answers_completed_at: string | null;
  readonly owner_notes: string | null;
  readonly owner_notes_updated_at: string | null;
  /**
   * 2026-10-24 (trainer-review-call-time) — the review call: `state` is null
   * until a list of times went out.
   */
  readonly call: {
    readonly state: "offered" | "proposed" | "confirmed" | "reoffer_due" | "unarranged" | null;
    /** The picked or confirmed time. */
    readonly starts_at: string | null;
    readonly minutes: number | null;
    /** How many times the current list holds. */
    readonly offer_count: number;
    /** How many lists have been written. */
    readonly offers: number;
    readonly confirmation_sent: boolean;
  };
}

/** GET /api/trainer-performance/reviews — admins only. */
export interface TrainerReviewsResponse {
  readonly month: string;
  readonly reviews: ReadonlyArray<TrainerReviewSummary>;
}

export interface InboxOpenCommitment {
  readonly id: string;
  readonly at: string;
  readonly summary: string;
  readonly detail: string | null;
}

/** GET /api/inbox/threads/:id — one matter's overview half. */
export interface InboxThreadDetailResponse {
  readonly thread: InboxThread;
  readonly timeline: ReadonlyArray<InboxTimelineEntry>;
  readonly open_commitments: ReadonlyArray<InboxOpenCommitment>;
  readonly computed_at: string;
}

// --- Trainer detail page (GET /api/trainers/:id) -----------------------------
// Mirrors the server's `trainer-detail.ts` shapes. Member names + session-note
// bodies are PII (staff-facing, same as the member page), which is why these
// live here + in the repo rather than in the PII-free shared types.

export interface TrainerDetailAccount {
  readonly id: string;
  readonly display_name: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly status: string;
  readonly metadata: Record<string, unknown>;
  readonly created_at: string;
  readonly updated_at: string;
}

export interface TrainerDetailMetrics {
  readonly sessions_completed: number;
  readonly distinct_members: number;
  readonly repeat_clients: number;
  readonly active_clients_90d: number;
  readonly upcoming_sessions: number;
  readonly next_session_at: string | null;
  readonly trial_to_customer_rate: number | null;
  readonly trial_to_customer_numerator: number;
  readonly trial_to_customer_denominator: number;
  readonly rebooking_rate: number | null;
  readonly membership_upsell_rate: number | null;
  readonly satisfaction_avg: number | null;
  readonly satisfaction_count: number;
}

export interface TrainerDetailSession {
  readonly id: string;
  readonly slot_id: string;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly status: string;
  readonly attendance_status: string;
  readonly session_type: string | null;
  readonly location: string | null;
  readonly member_id: string;
  readonly member_name: string | null;
}

export interface TrainerDetailMemberTrained {
  readonly member_id: string;
  readonly member_name: string | null;
  readonly lifecycle: "trial" | "customer";
  readonly sessions_with_trainer: number;
  readonly first_session_at: string | null;
  readonly purchase_count: number;
  readonly is_repeat: boolean;
  readonly first_with_trainer: boolean;
}

export interface TrainerDetailAvailabilityWindow {
  readonly weekday: number;
  readonly start_time: string;
  readonly end_time: string;
  readonly location: string | null;
  readonly session_type: string | null;
  readonly capacity: number;
  readonly status: string;
}

export interface TrainerDetailSlot {
  readonly id: string;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly capacity: number;
  readonly taken_count: number;
  readonly location: string | null;
  readonly status: string;
  readonly session_type: string | null;
}

export interface TrainerDetailSessionNote {
  readonly id: string;
  readonly booking_id: string | null;
  readonly member_id: string;
  readonly member_name: string | null;
  readonly body: string;
  readonly structured: Record<string, unknown>;
  readonly created_by_actor: string;
  readonly created_at: string;
}

export interface TrainerDetailMemory {
  readonly claude_md: string;
  readonly summary_md: string;
  readonly rolling_md: string;
  readonly longterm_md: string;
  readonly rolling_day_count: number;
  readonly last_entry_on: string | null;
  readonly last_recycled_at: string | null;
  readonly updated_at: string;
}

export interface TrainerDetailResponse {
  readonly trainer: TrainerDetailAccount;
  readonly metrics: TrainerDetailMetrics;
  readonly upcoming_sessions: ReadonlyArray<TrainerDetailSession>;
  readonly past_sessions: ReadonlyArray<TrainerDetailSession>;
  readonly members_trained: ReadonlyArray<TrainerDetailMemberTrained>;
  readonly availability: ReadonlyArray<TrainerDetailAvailabilityWindow>;
  readonly upcoming_slots: ReadonlyArray<TrainerDetailSlot>;
  readonly session_notes: ReadonlyArray<TrainerDetailSessionNote>;
  readonly memory: TrainerDetailMemory | null;
  readonly computed_at: string;
}

// --- Trainer Daily Brief (/today board + /trainers/:id/today + day-context) ---

export interface DayContextTubeLine {
  readonly name: string;
  readonly status: string;
  readonly severity: number;
  readonly reason: string | null;
}
export interface DayContextWeather {
  readonly available: true;
  readonly temp_c: number;
  readonly temp_max_c: number | null;
  readonly temp_min_c: number | null;
  readonly code: number;
  readonly label: string;
  readonly emoji: string;
  readonly precipitation_mm: number;
  readonly wind_kmh: number;
  readonly is_day: boolean;
}
export interface DayContextTube {
  readonly available: true;
  readonly all_good: boolean;
  readonly disrupted: ReadonlyArray<DayContextTubeLine>;
  readonly lines: ReadonlyArray<DayContextTubeLine>;
}
export interface DayContextSpecialDay {
  readonly available: true;
  readonly is_holiday_today: boolean;
  readonly today_holiday: string | null;
  readonly next_holiday: {
    readonly title: string;
    readonly date: string;
  } | null;
}
export interface DayContextResponse {
  readonly date_local: string;
  readonly status: "all_good" | "tube_disruption" | "holiday";
  readonly headline: string;
  readonly weather: DayContextWeather | { readonly available: false };
  readonly tube: DayContextTube | { readonly available: false };
  readonly special_day: DayContextSpecialDay | { readonly available: false };
  readonly computed_at: string;
}

export interface DailyBriefFlow {
  readonly flow_name: string;
  readonly current_step_index: number;
  readonly started_at: string;
}
export interface DailyBriefNote {
  readonly body: string;
  readonly created_at: string;
}
export interface DailyBriefMessage {
  readonly ts: string;
  readonly body: string | null;
  readonly dir: "in" | "out";
  readonly status: string | null;
}
export interface DailyBriefClient {
  readonly booking_id: string;
  readonly starts_at_local: string;
  readonly ends_at_local: string;
  readonly member_id: string;
  readonly member_name: string | null;
  readonly session_type: string | null;
  readonly location: string | null;
  readonly status: string;
  readonly lifecycle: "trial" | "customer";
  /** The member's live credit — the morning brief's "N credits left". */
  readonly remaining_credits: number;
  /** "⚠ low": exactly when the morning brief says low or "credits expiring soon". */
  readonly low_on_sessions: boolean;
  readonly memory: {
    readonly summary_md: string | null;
    readonly longterm_md: string | null;
  } | null;
  readonly recent_notes: ReadonlyArray<DailyBriefNote>;
  readonly active_flows: ReadonlyArray<DailyBriefFlow>;
  readonly consultation_profile: Record<string, unknown> | null;
  /** Webchat onboarding answers, prefix-stripped + cleaned server-side. */
  readonly onboarding_profile: Record<string, string> | null;
}
export interface TrainerTodayResponse {
  readonly trainer: TrainerDetailAccount;
  readonly studio_timezone: string;
  readonly briefing_date_local: string;
  readonly briefing_date_label: string;
  readonly clients_today: ReadonlyArray<DailyBriefClient>;
  readonly messages: ReadonlyArray<DailyBriefMessage>;
  readonly computed_at: string;
}
export interface TodayBoardClient {
  readonly starts_at_local: string;
  readonly member_id: string;
  readonly member_name: string | null;
  readonly lifecycle: "trial" | "customer";
  readonly active_flow_count: number;
}
export interface TodayBoardTrainer {
  readonly trainer_id: string;
  readonly display_name: string | null;
  readonly session_count: number;
  readonly first_at_local: string | null;
  readonly last_at_local: string | null;
  readonly clients: ReadonlyArray<TodayBoardClient>;
}
export interface TodayBoardResponse {
  readonly studio_timezone: string;
  readonly briefing_date_local: string;
  readonly briefing_date_label: string;
  readonly trainers: ReadonlyArray<TodayBoardTrainer>;
  readonly computed_at: string;
}

// ---- /tasks board ----
/** Derived per-task status dot — computed from charter + activity on every read
 *  (never stale). See `deriveTaskStatusSignal` in the tasks-board repository. */
export type TaskStatusSignal =
  | "done"
  | "in_progress"
  | "onboarding"
  | "ready"
  | "cancelled";
export interface TasksBoardTask {
  readonly id: string;
  /** @Task:<ref> handle (migration 085) — a short kebab id, or null. */
  readonly ref: string | null;
  readonly title: string;
  readonly category: string | null;
  readonly status: string;
  readonly due_at: string | null;
  readonly description: string | null;
  /** Shared-task group slug (migration 111); null ⇒ a solo 1:1 task. */
  readonly group_ref: string | null;
  /** Durable operator priority pin (migration 115); null ⇒ not pinned. Non-null ⇒
   *  the card shows a 📌 badge and sorts first on its lane. */
  readonly pinned_at: string | null;
  readonly status_signal: TaskStatusSignal;
  /** Work-time meta (2026-07-14): from pa_task_context (min/max created_at) with a
   *  `created_at` fallback for un-worked tasks; `work_summary` = latest note /
   *  charter goal / description, clipped. See lib/time-ago.ts. */
  readonly created_at: string;
  readonly work_started: string | null;
  readonly last_worked: string | null;
  readonly work_summary: string | null;
}
export interface TasksBoardAssignee {
  readonly kind: "manager" | "trainer";
  readonly id: string;
  readonly display_name: string | null;
  readonly tasks: ReadonlyArray<TasksBoardTask>;
}
export interface TasksBoardResponse {
  readonly managers: ReadonlyArray<TasksBoardAssignee>;
  readonly trainers: ReadonlyArray<TasksBoardAssignee>;
  /** Phase C — the general/unassigned queue (drag onto a card to assign). */
  readonly unassigned: ReadonlyArray<TasksBoardTask>;
  readonly computed_at: string;
}

// ---- /tasks/:id single-pane tracker (mirrors server task-detail.ts) ----
// Carries staff-visible PII (task title / description, context content, message
// bodies) — same posture as the member/trainer detail shapes above, which is
// why it lives here rather than the PII-free shared types.
export type TaskDetailAssigneeKind =
  | "trainer"
  | "staff"
  | "member"
  | "unassigned";
export interface TaskDetailHeader {
  readonly id: string;
  readonly ref: string | null;
  readonly title: string;
  readonly category: string | null;
  readonly status: string;
  readonly due_at: string | null;
  readonly description: string | null;
  readonly assignee_kind: TaskDetailAssigneeKind;
  readonly assignee_id: string | null;
  readonly assignee_name: string | null;
  readonly created_by: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}
export type TaskTimelineEntry =
  | {
      readonly type: "work";
      readonly at: string;
      readonly author: string;
      readonly content: string;
      readonly window_id: string;
      readonly seq: number;
    }
  | {
      readonly type: "action";
      readonly at: string;
      readonly action: string;
      readonly actor: string;
      readonly outcome: string;
      readonly action_kind: "mutation" | "read";
    }
  | {
      readonly type: "approval";
      readonly at: string;
      readonly pending_action_id: string;
      readonly action: string;
      readonly summary: string;
      readonly status: string;
      readonly channel: string | null;
      readonly expires_at: string | null;
      readonly resolved_at: string | null;
    }
  | {
      readonly type: "message";
      readonly at: string;
      readonly direction: "inbound" | "outbound";
      readonly message_id: string;
      readonly channel: string;
      readonly recipient: string;
      readonly body: string;
      readonly status: string;
      readonly source: string | null;
      readonly sent_at: string | null;
    };
export interface TaskFile {
  readonly kind: "deliverable" | "resource";
  readonly rel_path: string;
  readonly content: string | null;
  readonly bytes: number;
  readonly truncated: boolean;
  readonly modified_at: string | null;
  readonly synced_at: string;
}
/** Onboarding charter (migration 109) — the synthesised 6-field brief. */
export interface TaskCharter {
  readonly goal: string;
  readonly success_criteria: string;
  readonly tools_systems: string;
  readonly data_needed: string;
  readonly constraints: string;
  readonly approver_stakeholders: string;
  readonly created_by: string | null;
  readonly updated_at: string;
  readonly complete: boolean;
}
/** One onboarding question (pa_questions), linked by origin_task_id. */
export interface TaskOnboardingQuestion {
  readonly id: string;
  readonly topic_key: string;
  readonly question_text: string;
  readonly status: string;
  readonly answer_text: string | null;
  readonly answered_at: string | null;
  readonly created_at: string;
}
export interface TaskDetailResponse {
  readonly task: TaskDetailHeader;
  readonly timeline: ReadonlyArray<TaskTimelineEntry>;
  /** Laptop-synced deliverables (outputs) + resources (inputs), migration 105. */
  readonly files: ReadonlyArray<TaskFile>;
  /** Onboarding brief (migration 109) — charter + the Q&A that fed it, merged
   *  into ONE per-field view in the UI. */
  readonly charter: TaskCharter | null;
  readonly onboarding: ReadonlyArray<TaskOnboardingQuestion>;
  /** Charter filled AND no onboarding question still open — the §13 gate. */
  readonly onboarding_complete: boolean;
  readonly computed_at: string;
}

/**
 * Whether the laptop runner that APPLIES a saved worker override is ticking.
 * Derived server-side from the `layer-pa-worker` heartbeat + the `/status`
 * capability catalog, so this card and the `/status` tile cannot disagree.
 */
export interface PreloadRunner {
  readonly capability_id: string;
  readonly state: "ok" | "stale" | "never";
  readonly last_tick_at: string | null;
  readonly ok: boolean | null;
  readonly stale_after_hours: number | null;
}

export interface PreloadWorkerFile {
  readonly file: "CLAUDE.md" | "SKILL.md";
  readonly baseline: string;
  readonly override: string | null;
  readonly effective: string;
  readonly updated_at: string | null;
  readonly updated_by: string | null;
}

// ---------------------------------------------------------------------------
// /agents — the fleet board and its per-lane activity feed.
//
// Hand-mirrored from apps/dashboard/src/repositories/agents-board.ts and
// agents-activity.ts, the house pattern for board endpoints (src/types.ts holds
// only whoami/catalog/sagas/dispatch/data).
// ---------------------------------------------------------------------------

/**
 * The four resting states are deliberately NOT `degraded`. A lane nobody
 * watches, one stood down on purpose, one with nothing to do and one that has
 * simply not reached its first run are all things a board must state plainly
 * rather than colour red.
 *
 * They are four rather than one (`dormant`, until 2026-08-12) because they take
 * four different responses: load a launchd job, wait for the calendar, do
 * nothing, or go and add an observer.
 */
export type AgentLaneStatus =
  | "live"
  | "paused"
  | "degraded"
  | "stood_down"
  | "idle"
  | "not_yet_run"
  | "unobserved";

export interface AgentHeartbeat {
  readonly tested_at: string;
  readonly ok: boolean;
  readonly error_code: string | null;
  readonly detail: string | null;
  readonly freshness: "ok" | "stale" | "never";
}

export type AgentLaneControl =
  | {
      readonly kind: "agent_control";
      readonly agent_key: string;
      readonly desired_state: "active" | "paused";
      readonly reason: string | null;
      readonly updated_at: string | null;
      readonly updated_by: string | null;
      /** false ⇒ resuming here cannot start the lane; its launchd agent is unloaded. */
      readonly remote_start_effective: boolean;
      readonly launchd_label: string;
    }
  | {
      readonly kind: "connector";
      readonly connector: string;
      readonly enabled: boolean;
      readonly autonomy: string | null;
      readonly capability: string | null;
    }
  | {
      readonly kind: "studio_config_bool";
      readonly path: string;
      readonly enabled: boolean;
      /**
       * Mirrors `AgentLaneControl` in
       * `apps/dashboard/src/repositories/agents-board.ts` — the capability axis
       * when a connector descriptor backs this flag-gated lane (webchat),
       * `null` when none does. Non-null makes the page render the three-state
       * chat row instead of a bare checkbox.
       */
      readonly capability: string | null;
    }
  | { readonly kind: "none"; readonly reason: string };

export interface AgentLaneRow {
  readonly id: string;
  readonly label: string;
  /** One sentence on what this lane is FOR. */
  readonly description: string;
  readonly runtime: string;
  readonly runtime_home: string;
  readonly code_root: string;
  readonly capability_id: string | null;
  readonly status: AgentLaneStatus;
  readonly paused_by: "flag" | "sentinel" | null;
  readonly control_unread: boolean;
  /** Flag cleared, but the lane has not ticked since — not running yet. */
  readonly resume_pending: boolean;
  readonly heartbeat: AgentHeartbeat | null;
  readonly control: AgentLaneControl;
  readonly preload_agent: string | null;
  readonly last_activity_at: string | null;
  readonly has_activity_feed: boolean;
  /** "probe" = a real liveness signal; "activity" = inferred from a dispatch. */
  readonly observed_via: "probe" | "activity" | null;
}

export interface AgentFamilyRow {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly board_href: string | null;
  readonly dormant_since: string | null;
  readonly docs: readonly string[];
  readonly status: AgentLaneStatus;
  readonly headline: string;
  readonly lanes: readonly AgentLaneRow[];
}

export interface AgentsBoardResponse {
  readonly families: readonly AgentFamilyRow[];
  readonly summary: {
    readonly families: number;
    readonly lanes: number;
    readonly live: number;
    readonly paused: number;
    readonly degraded: number;
    readonly stood_down: number;
    readonly idle: number;
    readonly not_yet_run: number;
    readonly unobserved: number;
  };
  readonly controls_available: boolean;
  readonly generated_at: string;
}

export interface AgentActivityRow {
  readonly id: string;
  readonly occurred_at: string;
  readonly actor: string;
  readonly action: string;
  readonly outcome: string | null;
}

export interface AgentActivityResponse {
  readonly agent: string;
  readonly rows: readonly AgentActivityRow[];
  readonly next_cursor: string | null;
  readonly window_days: number;
  /** Set when the lane leaves no audit trail at all — a reason, not an error. */
  readonly unavailable_reason: string | null;
}

export interface PreloadAgentCard {
  readonly agent: string;
  readonly label: string;
  readonly files: ReadonlyArray<PreloadWorkerFile>;
  readonly runner: PreloadRunner | null;
}

export interface PreloadResponse {
  readonly cloud: {
    readonly label: string;
    readonly baseline: string;
    readonly edit_hint: string;
  };
  readonly worker: {
    readonly label: string;
    readonly editable: true;
    readonly files: ReadonlyArray<PreloadWorkerFile>;
    readonly runner: PreloadRunner;
  };
  readonly builder: {
    readonly label: string;
    readonly editable: false;
    readonly note: string;
  };
  /** Every dashboard-editable agent (worker | peter | inbox | trainerops). */
  readonly agents: ReadonlyArray<PreloadAgentCard>;
  readonly computed_at: string;
}

export interface ChatEfficiencyDay {
  readonly date: string;
  readonly request_count: number;
  readonly avg_turns: number | null;
  readonly avg_tool_calls: number | null;
  readonly total_cost_usd: number;
  readonly avg_latency_ms: number | null;
  readonly error_count: number;
  readonly avg_correctness: number | null;
  readonly avg_efficiency: number | null;
  readonly avg_truthfulness: number | null;
  readonly avg_tone: number | null;
  readonly avg_goal_completion: number | null;
  readonly avg_escalation: number | null;
  readonly avg_memory_capture: number | null;
}
export interface ChatEfficiencyRequest {
  readonly saga_run_id: string;
  readonly occurred_at: string;
  readonly interaction_date: string;
  readonly turn_count: number;
  readonly tool_call_count: number;
  readonly tool_names: ReadonlyArray<string>;
  readonly total_cost_usd: number;
  readonly total_latency_ms: number;
  readonly had_error: boolean;
  readonly model: string;
  readonly correctness_score: number | null;
  readonly efficiency_score: number | null;
  readonly truthfulness_score: number | null;
  readonly tone_score: number | null;
  readonly goal_completion_score: number | null;
  readonly escalation_score: number | null;
  readonly memory_capture_score: number | null;
  readonly judge_summary: string | null;
  readonly failure_tag: string | null;
  readonly improvement: string | null;
  readonly judged: boolean;
  /**
   * G02 — the run's chat text was erased with its member: no verdict text, no
   * tool names, and it cannot be judged. The numbers and any scores stay.
   */
  readonly erased: boolean;
}
export interface ChatEfficiencyResponse {
  readonly daily: ReadonlyArray<ChatEfficiencyDay>;
  readonly recent: ReadonlyArray<ChatEfficiencyRequest>;
  readonly computed_at: string;
  /**
   * The daily trend is filtered to the CURRENT judge rubric generation, so a
   * rubric rewrite is never silently read as an agent improvement/regression.
   */
  readonly trend_judge_prompt_version: string;
  /** Scored rows in the window dropped from the trend for carrying an older/NULL rubric. */
  readonly trend_excluded_rows: number;
}

// ---- Website analytics (/analytics — 2026-07-24) ----------------------------
export type WebsiteAnalyticsSource = "meta_pixel" | "clarity" | "ga4";
/** The newest stored snapshot for one source (payload shape is per-source). */
export interface WebsiteAnalyticsLatestSnapshot {
  readonly source: WebsiteAnalyticsSource;
  /** The day the snapshot describes (YYYY-MM-DD). */
  readonly captured_on: string;
  /** Connector-normalised payload — or `{error: <code>}` on a failed pull. */
  readonly payload: Record<string, unknown>;
}
/** One cross-row Clarity trend point (its API window is only 1..3 days). */
export interface ClarityTrendPoint {
  readonly captured_on: string;
  readonly sessions: number | null;
  readonly rage_clicks: number | null;
  readonly dead_clicks: number | null;
  readonly avg_scroll_depth: number | null;
}
/** Lane C (migration 153) — the last-7-days checkout funnel (first-party). */
export interface CheckoutFunnelSummary {
  readonly window_days: number;
  readonly started: number;
  readonly succeeded: number;
  readonly failed: number;
  readonly abandoned: number;
  /** Lost money per currency (lower-case code, A–Z); empty ⇒ nothing lost. */
  readonly lost_by_currency: ReadonlyArray<{
    readonly currency: string;
    readonly amount_minor: number;
  }>;
  /**
   * The lost checkouts by offer name — the name the recovery email and the
   * checkout alert use, else the raw slug — most first (2026-10-02).
   */
  readonly top_lost_offers: ReadonlyArray<{
    readonly name: string;
    readonly count: number;
  }>;
}
/**
 * Migration 205 — how a group of website checkouts was classified: a Google
 * click id, else a Meta click id, else a UTM source, else a referrer host,
 * else nothing. `meta` covers organic Facebook / Instagram links as well as
 * paid ads; `direct` also covers checkouts from before attribution was sent.
 */
export type PurchaseSourceChannel =
  | "google_ads"
  | "meta"
  | "tagged"
  | "referral"
  | "direct";
export interface PurchaseSourceRow {
  readonly channel: PurchaseSourceChannel;
  readonly source: string | null;
  readonly medium: string | null;
  /** NULL when the ad's landing URL carried no `utm_campaign`. */
  readonly campaign: string | null;
  readonly started: number;
  readonly paid: number;
  readonly paid_amount_minor: number;
  /** Of `paid`: the buyer's first purchase (2026-10-14). */
  readonly paid_new: number;
  /** Of `paid_new`: the ad click came before the buyer had an account. */
  readonly paid_found_by_ad: number;
  /** Of `paid`: a buyer who had bought before. */
  readonly paid_returning: number;
}
/** Migration 205 — website package purchases by ad / traffic source. */
export interface PurchaseSourceSummary {
  readonly window_days: number;
  readonly rows: ReadonlyArray<PurchaseSourceRow>;
}
/**
 * Migration 225 — the booking panel's step totals, counted by Layer itself.
 * One row per (step, flow, kind of browser); both windows end today and are
 * whole days in the studio's time zone. Order and labels come from
 * `@layer/canonical-data/booking-panel-steps`, not from this payload.
 */
export interface PanelStepCountRow {
  readonly step: string;
  readonly flow: string;
  readonly visitor_class: string;
  readonly last_7_days: number;
  readonly last_28_days: number;
}
export interface PanelStepSummary {
  /** The first day that holds a count, or null while nothing was counted. */
  readonly first_day: string | null;
  readonly rows: ReadonlyArray<PanelStepCountRow>;
}
/**
 * 2026-08-06 (migration 155) — the newest Instagram snapshot from the
 * `instagram_insights_daily` cron.
 *
 * ⚠️ `captured_on` is the day the CRON RAN, not the day the figures describe:
 * Instagram's insight buckets close at the account's own reporting hour
 * (07:00Z for @epulsive), so the numbers trail the run day. The payload's
 * `day_end_time` is Meta's own label for the bucket — render THAT, not
 * `captured_on`, when saying which day the figures cover.
 */
export interface InstagramInsightsLatestSnapshot {
  readonly captured_on: string;
  /** Connector-normalised payload — or `{error: <code>}` on a failed pull. */
  readonly payload: Record<string, unknown>;
}
/** One cross-row Instagram trend point. */
export interface InstagramTrendPoint {
  readonly captured_on: string;
  readonly followers: number | null;
  readonly reach: number | null;
  readonly interactions: number | null;
}
/**
 * 2026-08-06 (migration 156) — one story, from `instagram_story_insights`.
 *
 * NOT part of the snapshot payload: a story is keyed by its own id and is
 * observed by several cron runs across its 24h life, so it outlives any one
 * day's snapshot. `observations` is how many runs caught it alive, which is
 * the cadence's own witness — a story seen once means the poll barely made it.
 */
export interface InstagramStorySummary {
  readonly story_id: string;
  readonly permalink: string | null;
  readonly posted_at: string | null;
  readonly last_seen_at: string;
  readonly observations: number;
  readonly metrics: Record<string, number>;
  readonly metrics_error: string | null;
}
export interface WebsiteAnalyticsResponse {
  readonly latest: {
    readonly meta_pixel: WebsiteAnalyticsLatestSnapshot | null;
    readonly clarity: WebsiteAnalyticsLatestSnapshot | null;
    readonly ga4: WebsiteAnalyticsLatestSnapshot | null;
  };
  readonly clarity_trend: ReadonlyArray<ClarityTrendPoint>;
  readonly checkout_funnel: CheckoutFunnelSummary;
  readonly purchase_sources: PurchaseSourceSummary;
  readonly panel_steps: PanelStepSummary;
  readonly instagram: InstagramInsightsLatestSnapshot | null;
  readonly instagram_trend: ReadonlyArray<InstagramTrendPoint>;
  readonly instagram_stories: ReadonlyArray<InstagramStorySummary>;
  readonly computed_at: string;
}

// ---- Config suggestions (phase 89; mirrors chat-efficiency repo) -----------
export interface ChatConfigSuggestionChange {
  readonly lever: string;
  readonly target_field: string;
  readonly before_value: string | ReadonlyArray<string> | null;
  readonly after_value: string | ReadonlyArray<string> | null;
}
/**
 * Migration 076 — the post-apply before/after re-measurement written onto an
 * applied suggestion (the loop's back-half). NULL until first measured.
 */
export interface ChatConfigSuggestionOutcomeAxes {
  readonly truthfulness: number | null;
  readonly correctness: number | null;
  readonly goalCompletion: number | null;
  readonly n: number;
}
export interface ChatConfigSuggestionOutcomeSnapshot {
  readonly tier: string;
  readonly judge_prompt_version: string | null;
  readonly window_days: number;
  readonly horizon_days: number;
  readonly before: ChatConfigSuggestionOutcomeAxes;
  readonly after: ChatConfigSuggestionOutcomeAxes;
  readonly delta: {
    readonly truthfulness: number | null;
    readonly correctness: number | null;
    readonly goal_completion: number | null;
  };
  readonly sufficient: boolean;
}
export interface ChatConfigSuggestionView {
  readonly id: string;
  readonly ref: string;
  readonly status: string;
  readonly changes: ReadonlyArray<ChatConfigSuggestionChange>;
  readonly rationale: string;
  readonly expected_impact: Record<string, string> | null;
  readonly priority: number;
  readonly eval_pass_rate: number | null;
  readonly eval_baseline_rate: number | null;
  readonly eval_passed: boolean | null;
  readonly metrics_snapshot: Record<string, unknown>;
  readonly outcome_snapshot: ChatConfigSuggestionOutcomeSnapshot | null;
  readonly expires_at: string;
  readonly applied_at: string | null;
  readonly applied_by: string | null;
  readonly created_at: string;
}
export interface ChatConfigSuggestionsResponse {
  readonly pending: ReadonlyArray<ChatConfigSuggestionView>;
  readonly history: ReadonlyArray<ChatConfigSuggestionView>;
  readonly computed_at: string;
}

// ---- Knowledge-base suggestions (migration 077; Stage-4 Artifact Loop) ------
// Mirrors `KnowledgeBaseSuggestionSummary` /
// `ListPendingKnowledgeBaseEntriesOutput` in
// `@layer/canonical-actions/actions/list-pending-knowledge-base-entries`. Read
// via the generic dispatch surface (`list_pending_knowledge_base_entries`), not
// a dedicated GET route.
export interface KnowledgeBaseSuggestionSummary {
  readonly suggestion_id: string;
  readonly ref: string;
  readonly status: string;
  readonly topic: string;
  readonly content: string;
  readonly rationale: string;
  readonly source_saga_run_ids: ReadonlyArray<string>;
  readonly expires_at: string;
  readonly created_at: string;
}
export interface ListPendingKnowledgeBaseEntriesOutput {
  readonly pending_suggestions: ReadonlyArray<KnowledgeBaseSuggestionSummary>;
}

// ---- Status page (mirrors apps/dashboard/src/repositories/status.ts) -------
export type StatusHealth = "ok" | "stale" | "failed" | "never" | "dark";
export type StatusUnitKind = "action" | "saga" | "cron" | "connector" | "webhook";
export interface StatusUnitDetail {
  readonly unit: string;
  readonly kind: StatusUnitKind;
  readonly last_at: string | null;
  readonly ok: boolean | null;
  readonly status: string | null;
}
export interface StatusCapabilityState {
  readonly id: string;
  readonly name: string;
  readonly category: string;
  readonly description: string;
  readonly safety: "safe-auto" | "safe-manual" | "passive-only";
  readonly probe: string;
  readonly health: StatusHealth;
  readonly last_run_at: string | null;
  readonly last_self_test_at: string | null;
  readonly self_test_ok: boolean | null;
  readonly self_test_detail: string | null;
  readonly error_code: string | null;
  readonly units: ReadonlyArray<string>;
  readonly unit_details: ReadonlyArray<StatusUnitDetail>;
}
export interface StatusCategoryGroup {
  readonly category: string;
  readonly capabilities: ReadonlyArray<StatusCapabilityState>;
}
export interface StatusCatchAllEntry {
  readonly name: string;
  readonly kind: StatusUnitKind;
  readonly last_at: string | null;
  readonly ok: boolean | null;
  readonly status: string | null;
}
export interface StatusSummary {
  readonly ok: number;
  readonly stale: number;
  readonly failed: number;
  readonly never: number;
  readonly dark: number;
  readonly total: number;
}
export interface StatusResponse {
  readonly categories: ReadonlyArray<StatusCategoryGroup>;
  readonly catch_all: ReadonlyArray<StatusCatchAllEntry>;
  readonly summary: StatusSummary;
  readonly computed_at: string;
}

/** 2026-09-23 — GET /api/ai-armed. */
export interface AiArmedRow {
  readonly channel: string;
  readonly member_id: string;
  readonly display_name: string | null;
  readonly email: string | null;
  readonly armed_at: string;
}
export interface AiArmedResponse {
  readonly ok: boolean;
  readonly armed: ReadonlyArray<AiArmedRow>;
}

export const api = {
  whoami: () => authedGet<WhoamiResponse>("/api/whoami"),
  // Chat efficiency — daily rollup + live recent feed.
  chatEfficiency: () =>
    authedGet<ChatEfficiencyResponse>("/api/efficiency"),
  // Website analytics — latest Meta Pixel / Clarity / GA4 snapshots + trend.
  analytics: () => authedGet<WebsiteAnalyticsResponse>("/api/analytics"),
  // On-demand "Judge now" — judge one request + return the updated row.
  judgeChatRequest: (sagaRunId: string) =>
    authedPost<ChatEfficiencyRequest>("/api/efficiency/judge", {
      saga_run_id: sagaRunId,
    }),
  // Phase 89 — chat-config optimizer suggestions (pending + recent history).
  chatConfigSuggestions: () =>
    authedGet<ChatConfigSuggestionsResponse>("/api/efficiency/suggestions"),
  // Status board — per-capability health + last self-test.
  status: () => authedGet<StatusResponse>("/api/status"),
  // "Run test now" — actively re-test ONE capability. `confirmSend` must be
  // true for synthetic-comms capabilities (sends a real admin-only message).
  runStatusTest: (capabilityId: string, confirmSend = false) =>
    authedPost<StatusCapabilityState>("/api/status/run-test", {
      capability_id: capabilityId,
      confirm_send: confirmSend,
    }),
  // Member detail page — one member's full aggregate.
  member: (id: string) =>
    authedGet<MemberDetailResponse>(`/api/members/${encodeURIComponent(id)}`),
  // The focused conversation payload (thread + AI pills + recipient phone)
  // for the Conversation panel's live reply composer + AI-pause toggle.
  memberConversation: (id: string) =>
    authedGet<MemberConversationResponse>(
      `/api/members/${encodeURIComponent(id)}/conversation`,
    ),
  // WhatsApp picture intake — the Members page's "Forms waiting for a member".
  pictureIntakesWaiting: () =>
    authedGet<WaitingPictureIntakesResponse>("/api/members/picture-intakes/waiting"),
  // A consultation form's photo, as a URL signed for five minutes. Fetched on
  // each open, never cached: the URL expires, and is a bearer credential.
  pictureIntakePhoto: (id: string) =>
    authedGet<PictureIntakePhotoResponse>(
      `/api/members/picture-intakes/${encodeURIComponent(id)}/photo`,
    ),
  // The calling staff user's saved layout for a dashboard page (card order /
  // sizes / hidden cards / journey filter). `preferences` is null when none.
  dashboardLayout: (page: string) =>
    authedGet<DashboardLayoutResponse>(
      `/api/dashboard-layout?page=${encodeURIComponent(page)}`,
    ),
  // Trainer detail page — one trainer's full aggregate.
  trainer: (id: string) =>
    authedGet<TrainerDetailResponse>(`/api/trainers/${encodeURIComponent(id)}`),
  // Mint one trainer's provisional-pay link. Returns a PATH, not a URL — the
  // caller prefixes `window.location.origin`, which is by construction the
  // origin serving the page, so no hardcoded host can drift.
  trainerPayLink: (id: string) =>
    authedPost<{ ok: true; path: string; expires_at: string }>(
      `/api/trainers/${encodeURIComponent(id)}/pay-link`,
      {},
    ),
  // All-trainers roster for the day. The `/today` board page it was built for
  // was retired; the trainer switcher on `/trainers/:id/today` is the caller
  // now — which is why the endpoint outlived the page.
  todayBoard: () => authedGet<TodayBoardResponse>("/api/today"),
  tasksBoard: () => authedGet<TasksBoardResponse>("/api/tasks-board"),
  // Task detail — one task's header + merged timeline (work / actions /
  // approvals / messages) for the `/tasks/:id` single-pane tracker.
  taskDetail: (id: string) =>
    authedGet<TaskDetailResponse>(`/api/tasks/${encodeURIComponent(id)}`),
  // PR board — engagements by pipeline stage + overview counters + every PR
  // contact (the `/pr` page). People link to the profile below.
  prBoard: () => authedGet<PrBoardResponse>("/api/pr-board"),
  // PR contact profile — one contact's header + full cross-channel thread
  // (rendered by `/pr/people/:id`).
  outreachDetail: (id: string) =>
    authedGet<OutreachDetailResponse>(`/api/outreach/${encodeURIComponent(id)}`),
  // Inbox board — operator-mail matters by stage + counters + tab strip +
  // correspondents (the `/inbox` page).
  inboxBoard: () => authedGet<InboxBoardResponse>("/api/inbox-board"),
  // Decisions board — open decisions with per-round ballots + the resolved /
  // cancelled recents (the `/decisions` page).
  decisionsBoard: () =>
    authedGet<DecisionsBoardResponse>("/api/decisions-board"),
  // The /inbox WhatsApp + Instagram tabs — read-only, conversation-keyed.
  inboxChats: () => authedGet<ChatsBoardResponse>("/api/inbox-chats"),
  instagramComments: () =>
    authedGet<InstagramCommentsResponse>("/api/instagram-comments"),
  inboxChatThread: (channel: string, sender: string) =>
    authedGet<ChatThreadResponse>(
      `/api/inbox-chats/${encodeURIComponent(channel)}/${encodeURIComponent(sender)}`,
    ),
  rotaBoard: (month?: string) =>
    authedGet<RotaBoardResponse>(
      month !== undefined
        ? `/api/rota-board?month=${encodeURIComponent(month)}`
        : "/api/rota-board",
    ),
  payrollBoard: (month?: string) =>
    authedGet<PayrollBoardResponse>(
      month !== undefined
        ? `/api/payroll-board?month=${encodeURIComponent(month)}`
        : "/api/payroll-board",
    ),
  trainerPerformance: (month: string) =>
    authedGet<TrainerPerformanceResponse>(
      `/api/trainer-performance?month=${encodeURIComponent(month)}`,
    ),
  // The month's monthly reviews, their answers and the owner's notes —
  // admins only; anyone else gets 403 staff_not_admin (2026-10-23).
  trainerReviews: (month: string) =>
    authedGet<TrainerReviewsResponse>(
      `/api/trainer-performance/reviews?month=${encodeURIComponent(month)}`,
    ),
  // Save the owner's private notes on one trainer's review; "" clears them.
  saveTrainerReviewNotes: (body: {
    readonly trainer_id: string;
    readonly month: string;
    readonly notes: string;
  }) =>
    authedPost<{ ok: true; review_id: string | null; saved_at: string | null; cleared: boolean }>(
      "/api/trainer-performance/notes",
      body,
    ),
  // One trainer's monthly review PDF (2026-10-23, "Open PDF").
  trainerScorecardPdf: (month: string, trainerId: string) =>
    authedGetBlob(
      `/api/trainer-performance/scorecard.pdf?month=${encodeURIComponent(
        month,
      )}&trainer_id=${encodeURIComponent(trainerId)}`,
    ),
  // Inbox thread overview — the matter row + full timeline + open commitments
  // (the `/inbox/threads/:id` page; the person/conversation half rides
  // `outreachDetail` on the same row).
  inboxThreadDetail: (id: string) =>
    authedGet<InboxThreadDetailResponse>(
      `/api/inbox/threads/${encodeURIComponent(id)}`,
    ),
  // Preload editor — each PA's standing instructions (Phase 3c).
  preload: () => authedGet<PreloadResponse>("/api/preload"),
  // /agents — the fleet board: families, lanes, liveness, controls.
  agentsBoard: () => authedGet<AgentsBoardResponse>("/api/agents"),
  // 2026-09-23 — who the assistant will answer BY ITSELF on an opt-in channel
  // (email / Instagram). The inventory that did not exist when a pill armed on
  // 18 Sep went unnoticed for four days.
  aiArmed: () => authedGet<AiArmedResponse>("/api/ai-armed"),
  // One lane's (or family's) audit feed, keyset-paged like the Home card.
  // `agent` is a registry id; the SQL predicate is sourced there, never here.
  agentsActivity: (agent: string, cursor?: string | null, limit?: number) => {
    const qs = new URLSearchParams({ agent });
    if (cursor !== undefined && cursor !== null) qs.set("cursor", cursor);
    if (limit !== undefined) qs.set("limit", String(limit));
    return authedGet<AgentActivityResponse>(`/api/agents/activity?${qs.toString()}`);
  },
  // Trainer Daily Brief — one trainer's clients today with full context.
  trainerToday: (id: string) =>
    authedGet<TrainerTodayResponse>(
      `/api/trainers/${encodeURIComponent(id)}/today`,
    ),
  // Day-context header (weather + tube + holidays) for the Daily Brief.
  dayContext: () => authedGet<DayContextResponse>("/api/day-context"),
  catalog: () => authedGet<CatalogResponse>("/api/catalog"),
  sagas: () => authedGet<SagasResponse>("/api/sagas"),
  data: (
    entity: string,
    cursor: string | null,
    limit: number,
    extraParams?: Record<string, string>,
  ) => {
    const params = new URLSearchParams();
    if (cursor !== null) params.set("cursor", cursor);
    params.set("limit", String(limit));
    // Phase 38 sub-decision M — `?<col>=null` filter pass-through
    // (the endpoint validates against `NULL_FILTER_COLUMNS`). Allows
    // `/data/payments?member_id=null` orphan-payment inbox.
    if (extraParams !== undefined) {
      for (const [k, v] of Object.entries(extraParams)) {
        params.set(k, v);
      }
    }
    return authedGet<DataResponse>(`/api/data/${entity}?${params.toString()}`);
  },
  // Calendar page — slots (+ trainer + bookings) for a date window.
  // `from`/`to` are ISO instants ([from, to)); absent → current UTC month.
  calendar: (from: string, to: string) => {
    const params = new URLSearchParams();
    params.set("from", from);
    params.set("to", to);
    return authedGet<CalendarResponse>(`/api/calendar?${params.toString()}`);
  },
  // Calendar "Add availability" tool — parse free text into a structured
  // proposal (preview only; the SAVE dispatches set_trainer_availability +
  // generate_slots through the chokepoint via dispatchAction).
  parseAvailability: (text: string) =>
    authedPost<AvailabilityParseResponse>("/api/availability/parse", { text }),
  parseBulkSlotOps: (text: string, from: string, to: string) =>
    authedPost<SlotBulkParseResponse>("/api/slots/parse-bulk", {
      text,
      from,
      to,
    }),
  // Phase 41-followup — per-entity row counts for the /data sidebar badges.
  dataCounts: () =>
    authedGet<{
      counts: Record<string, number>;
      computed_at: string;
    }>("/api/data-counts"),
  lookupOptions: (entity: string, q?: string) =>
    authedGet<{
      entity: string;
      options: ReadonlyArray<{
        value: string;
        label: string;
        sublabel?: string;
        group?: string;
        // Catalog entities (packages / memberships) carry the offering's
        // list price so a client can pre-fill an amount field.
        price_minor?: number;
        currency?: string;
        // `packages` only — what the custom pack's validity pre-fill reads
        // (custom-pack-validity-at-sale).
        sessions_total?: number;
        validity_days?: number;
        session_type?: string;
        // `tags` only — how many active members carry the tag.
        member_count?: number;
      }>;
      count: number;
    }>(
      q !== undefined && q.trim().length > 0
        ? `/api/lookups/${entity}?q=${encodeURIComponent(q.trim())}`
        : `/api/lookups/${entity}`,
    ),
  // Phase 32b — generic GET wrapper for the new `/api/flows-stats`
  // endpoint. Keeps the per-page hooks simple while staying authed.
  get: <T>(path: string) => authedGet<T>(path),
  // Dev-only DB reset. Returns 404 in production builds (the endpoint
  // is bootstrap-gated on NODE_ENV); the caller treats that as "button
  // disabled, you're in prod".
  devReset: () =>
    authedPost<DevResetResponse>("/api/dev/reset", { confirm: "RESET" }),
  // Studio-scoped member purge. 404 unless the server runs with
  // LAYER_ALLOW_MEMBER_PURGE=true; the button that calls this is itself
  // rendered only when the SPA was built with LAYER_PUB_ALLOW_MEMBER_PURGE=1.
  purgeMembers: () =>
    authedPost<MemberPurgeResponse>("/api/dev/purge-members", {
      confirm: "PURGE",
    }),
  // Phase 41 followup — marketing-flow fast-forward toggle. Rewired
  // 2026-06-09 (prod-fast-forward-toggle) to a live DB-backed flag
  // (studio_config.marketing_flow_fast_forward) that works in PRODUCTION.
  devFastForwardGet: () =>
    authedGet<{
      ok: true;
      enabled: boolean;
      source: "db" | "default";
      receiver_pid: number | null;
    }>("/api/dev/fast-forward"),
  devFastForwardSet: (enabled: boolean) =>
    authedPost<{
      ok: true;
      enabled: boolean;
      signaled: boolean;
      signal_error?: string;
    }>("/api/dev/fast-forward", { enabled }),
  // Phase 41 — per-studio config editor helpers.
  getStudioConfig: (slug: string) =>
    authedGet<StudioConfigResponse>(
      `/api/studio-config/${encodeURIComponent(slug)}`,
    ),
  saveStudioConfig: (
    slug: string,
    body: {
      request_id: string;
      patch: Record<string, unknown>;
      sections_touched?: ReadonlyArray<string>;
    },
  ) =>
    authedPost<StudioConfigSaveResponse>(
      `/api/studio-config/${encodeURIComponent(slug)}`,
      body,
    ),
  reloadStudioConfig: (slug: string) =>
    authedPost<{ reloaded?: boolean; code?: string; mode?: string }>(
      `/api/studio-config/${encodeURIComponent(slug)}/reload`,
      {},
    ),
  // Migration 042 — DB-backed staff allowlist (WhatsApp human:staff
  // promotion + the phase-64 admin tier). Edits take effect live on the
  // next inbound message — no YAML edit + redeploy. NOT a canonical action.
  getStaffAllowlist: (slug: string) =>
    authedGet<{
      entries: ReadonlyArray<{
        id: string;
        phone: string;
        staff_id: string;
        display_name: string;
        admin: boolean;
        /** Migration 160 — per-person alert mute; orthogonal to `admin`. */
        alerts_muted: boolean;
        /** Migration 173 — takes part in staff decisions; default false. */
        voter: boolean;
        /**
         * Migration 192 — the per-person notification grid
         * `{ "<type>": ["whatsapp"|"telegram"|"email", ...] }`; an absent
         * type is OFF. Replaces the 17 `notify_*` boolean rails.
         */
        notification_grid: Record<string, string[]>;
        /** 2026-08-12 — `notify_staff`'s email leg; null when unset. */
        email: string | null;
        /** 2026-08-12 — Telegram identity: inbound staff resolution AND notify_staff's outbound Telegram leg. */
        telegram_chat_id: string | null;
        /** Migration 177 — the address they SIGN IN with (not the notify `email`); null when unbound. */
        login_email: string | null;
        /** Migration 178 — page grant-list; null = unrestricted, [] = restricted to nothing. */
        visible_pages: string[] | null;
      }>;
      studio_slug: string;
    }>(`/api/staff-allowlist/${encodeURIComponent(slug)}`),
  saveStaffAllowlist: (
    slug: string,
    entries: ReadonlyArray<{
      phone: string;
      staff_id: string;
      display_name: string;
      admin: boolean;
      alerts_muted: boolean;
      voter: boolean;
      /**
       * Migration 192 — the whole grid verbatim ({} = everything off).
       * OMIT the key to keep the stored grid (the stale-tab guard); there
       * is no null state.
       */
      notification_grid?: Readonly<Record<string, ReadonlyArray<string>>>;
      /** Send `null`/"" to CLEAR; omit the key to leave the stored value alone. */
      email?: string | null;
      telegram_chat_id?: string | null;
      /** Migration 177 — the dashboard login address. Same carry-over contract as the two above. */
      login_email?: string | null;
      /** Migration 178 — page grant-list. Omit = keep, null = unrestrict, [] = restrict to nothing. */
      visible_pages?: ReadonlyArray<string> | null;
    }>,
  ) =>
    authedPost<{
      ok: true;
      entries: ReadonlyArray<{
        id: string;
        phone: string;
        staff_id: string;
        display_name: string;
        admin: boolean;
        alerts_muted: boolean;
        voter: boolean;
        notification_grid: Record<string, string[]>;
        email: string | null;
        telegram_chat_id: string | null;
        login_email: string | null;
        visible_pages: string[] | null;
      }>;
      studio_slug: string;
    }>(`/api/staff-allowlist/${encodeURIComponent(slug)}`, { entries }),
  // 2026-08-24 — the /mcp page's OAuth-clients panel. List / create /
  // edit-scopes / revoke over `/api/oauth-clients/:slug`. The create
  // response's `client_secret` is plaintext shown ONCE — it is never
  // retrievable again (only the scrypt hash is stored).
  getOauthClients: (slug: string) =>
    authedGet<{
      ok: true;
      clients: ReadonlyArray<OauthClientRow>;
      studio_slug: string;
      viewer: OauthClientsViewer | null;
    }>(`/api/oauth-clients/${encodeURIComponent(slug)}`),
  createOauthClient: (slug: string, body: { name: string; scopes: ReadonlyArray<string> }) =>
    authedPost<{ ok: true; client: OauthClientRow; client_secret: string }>(
      `/api/oauth-clients/${encodeURIComponent(slug)}`,
      { op: "create", ...body },
    ),
  // 2026-10-09 — a connector that acts as the SIGNED-IN person. The server
  // binds it to the session's own staff row (there is no way to name anyone
  // else) and returns no secret: it connects with a connect code only.
  createPersonalOauthClient: (
    slug: string,
    body: { name: string; tier_cap: "staff" | null },
  ) =>
    authedPost<{ ok: true; client: OauthClientRow }>(
      `/api/oauth-clients/${encodeURIComponent(slug)}`,
      { op: "create", acts_as: "staff", ...body },
    ),
  updateOauthClientScopes: (
    slug: string,
    clientId: string,
    scopes: ReadonlyArray<string>,
  ) =>
    authedPost<{ ok: true; client: OauthClientRow }>(
      `/api/oauth-clients/${encodeURIComponent(slug)}`,
      { op: "update_scopes", client_id: clientId, scopes },
    ),
  // 2026-10-14 — switch a person-bound connector's tools on and off (its
  // person or an admin). A full replace of the switched-off list; empty
  // switches every tool back on. It bites on the connector's next request.
  // `expectedDisabledTools` is the list the edit started from: if another
  // save changed it since, nothing is written and this rejects with an
  // `ApiError` whose code is `oauth_client_tools_changed` (409).
  updateOauthClientTools: (
    slug: string,
    clientId: string,
    disabledTools: ReadonlyArray<string>,
    expectedDisabledTools: ReadonlyArray<string>,
  ) =>
    authedPost<{ ok: true; client: OauthClientRow }>(
      `/api/oauth-clients/${encodeURIComponent(slug)}`,
      {
        op: "update_tools",
        client_id: clientId,
        disabled_tools: disabledTools,
        expected_disabled_tools: expectedDisabledTools,
      },
    ),
  revokeOauthClient: (slug: string, clientId: string) =>
    authedPost<{ ok: true; client: OauthClientRow }>(
      `/api/oauth-clients/${encodeURIComponent(slug)}`,
      { op: "revoke", client_id: clientId },
    ),
  // 2026-08-25 — the ChatGPT lane. Mint a one-time connect code for a
  // grant; `connect_code` is shown ONCE (only its sha256 is stored).
  issueOauthConnectCode: (slug: string, clientId: string) =>
    authedPost<{
      ok: true;
      client: OauthClientRow;
      connect_code: string;
      connect_code_expires_at: string;
    }>(`/api/oauth-clients/${encodeURIComponent(slug)}`, {
      op: "issue_connect_code",
      client_id: clientId,
    }),
  // Phase 48 — `/config/:slug` section #9 "Vendor credentials" endpoints.
  getStudioSecrets: (slug: string) =>
    authedGet<{
      secrets: ReadonlyArray<{
        kind: string;
        present: boolean;
        updated_at: string | null;
      }>;
    }>(`/api/studio-secrets/${encodeURIComponent(slug)}`),
  saveStudioSecrets: (
    slug: string,
    body: {
      request_id: string;
      secrets: Record<string, string | null>;
    },
  ) =>
    authedPost<{
      ok: true;
      response: {
        updated: true;
        kinds_changed: ReadonlyArray<string>;
        kinds_deleted: ReadonlyArray<string>;
      };
      idempotency_replay_short_circuit?: true;
    }>(`/api/studio-secrets/${encodeURIComponent(slug)}`, body),
  revealStudioSecret: (
    slug: string,
    kind: string,
    body: { request_id: string },
  ) =>
    authedPost<{
      value: string | null;
      found: boolean;
      kind: string;
      idempotency_replay_short_circuit?: true;
    }>(
      `/api/studio-secrets/${encodeURIComponent(
        slug,
      )}/reveal/${encodeURIComponent(kind)}`,
      body,
    ),
  // 2026-08-25 — `/whatsapp-signup` Embedded Signup coexistence onboarding.
  // The completion PARKS credentials (token, phone, WABA id) as pending
  // kinds without subscribing webhooks; the explicit activate op subscribes
  // the app to the parked WABA and repoints production (runbook §6).
  getWhatsappSignupConfig: (slug: string) =>
    authedGet<{
      ok: true;
      configured: boolean;
      app_id?: string;
      config_id?: string;
      graph_version?: string;
      pending?: {
        access_token: boolean;
        phone_number_id: boolean;
        waba_id: boolean;
      } | null;
    }>(`/api/whatsapp-signup/${encodeURIComponent(slug)}`),
  completeWhatsappSignup: (
    slug: string,
    body: {
      request_id: string;
      code: string;
      waba_id: string;
      phone_number_id?: string;
    },
  ) =>
    authedPost<{
      ok: true;
      outcome: "pending_activation" | "phone_choice_required";
      waba_id: string;
      phone_number_id?: string;
      display_phone_number?: string | null;
      phone_numbers?: ReadonlyArray<{
        id: string;
        display_phone_number: string | null;
      }>;
      pending_stored: boolean;
      idempotency_replay_short_circuit?: true;
    }>(`/api/whatsapp-signup/${encodeURIComponent(slug)}`, body),
  selectWhatsappSignupPhone: (
    slug: string,
    body: { request_id: string; phone_number_id: string },
  ) =>
    authedPost<{
      ok: true;
      outcome: "pending_activation";
      waba_id: string;
      phone_number_id: string;
      display_phone_number: string | null;
      pending_stored: boolean;
      idempotency_replay_short_circuit?: true;
    }>(`/api/whatsapp-signup/${encodeURIComponent(slug)}`, {
      op: "select_phone",
      ...body,
    }),
  // wa-activate-template-check (2026-10-28) — read only: every template the
  // studio can send against the parked WABA (and today's for categories).
  // Activate refuses 409 `whatsapp_signup_templates_not_ready` until `ready`.
  checkWhatsappSignupTemplates: (slug: string, body: { request_id: string }) =>
    authedPost<{
      ok: true;
      ready: boolean;
      templates: ReadonlyArray<WhatsappSignupTemplateRow>;
      live_read: boolean;
    }>(`/api/whatsapp-signup/${encodeURIComponent(slug)}`, {
      op: "check_templates",
      ...body,
    }),
  activateWhatsappSignup: (slug: string, body: { request_id: string }) =>
    authedPost<{
      ok: true;
      outcome: "activated";
      activated: boolean;
      waba_id: string;
      phone_number_id: string;
      subscribed: true;
      // false when production is repointed but the parked rows could not
      // all be cleared; `warning` then says what that means.
      pending_cleared: boolean;
      warning?: string;
      idempotency_replay_short_circuit?: true;
    }>(`/api/whatsapp-signup/${encodeURIComponent(slug)}`, {
      op: "activate",
      ...body,
    }),
  // Checkbox-toggle followup — POST `/api/flows/:flow_name/toggle`
  // dispatches `set_flow_enabled` through the chokepoint. Toggling OFF
  // also bulk-flips every active member_flows row for the studio's
  // (flow_name) to status='opted_out' so members stuck mid-drip leave
  // immediately rather than waiting for the saga's next wake.
  setFlowEnabled: (flowName: string, body: { enabled: boolean; request_id: string }) =>
    authedPost<{
      ok: true;
      outcome: "ok";
      response: {
        flow_name: string;
        enabled: boolean;
        members_exited: number;
      };
      idempotency_replay_short_circuit?: boolean;
    }>(`/api/flows/${encodeURIComponent(flowName)}/toggle`, body),
};
